import { useState, useEffect, useCallback, useMemo } from "react";
import React from "react";
import { I18nProvider, useI18n } from "./i18n/I18nContext";
import { AppNavigation } from "./components/AppNavigation";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link, useSearchParams, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { computeDayGridBadges, buildUtsavaSpans, padMonthDays } from "@/lib/panchanga-grid";
import { locQueryString, tomorrowDateStr, todayDateStr } from "@/lib/panchanga-location";
import { usePanchangaLocation } from "@/hooks/usePanchangaLocation";
import { DashboardPanchangaCard } from "@/components/dashboard/DashboardPanchangaCard";
import { SacredLibraryHub } from "@/components/sacred-library/SacredLibraryHub";
import { SacredStaffBar } from "@/components/sacred-library/SacredStaffBar";
import { SacredTopicPage } from "@/components/sacred-library/SacredTopicPage";
import { SacredAdminStandalone } from "@/components/sacred-admin/SacredAdminStandalone";
import { SacredEditor } from "@/components/sacred-admin/SacredEditor";
import { SacredPagesHub } from "@/components/sacred-admin/SacredPagesHub";
import { PageEditor } from "@/components/sacred-admin/PageEditor";
import { CmsPageShell } from "@/components/sacred-pages/CmsPageShell";
import { useCmsPage, useCmsLabels } from "@/components/sacred-pages/CmsPageContext";
import { cmsJson, cmsText } from "@/lib/sacred-pages/cms-helpers";
import { KnowledgeTabCms } from "@/components/sacred-pages/KnowledgeTabCms";
import { AdminPanel } from "@/components/sacred-admin/AdminPanel";
import { TattvaTestPage } from "@/components/tattva/TattvaTestPage";
import { SacredProfileSettings } from "@/components/sacred-alerts/SacredProfileSettings";
import { TriLanguageSettings } from "@/components/i18n/TriLanguageSettings";
import { AlertsTestPage } from "@/components/sacred-alerts/AlertsTestPage";
import { JourneyTestPage } from "@/components/sacred-journey/JourneyTestPage";
import { JourneyValidationPage } from "@/components/sacred-journey/JourneyValidationPage";
import { JourneyDashboard } from "@/components/sacred-journey/JourneyDashboard";
import { AcharyaChat } from "@/components/acharya/AcharyaChat";
import { ErrorBoundary } from "@/components/acharya/ErrorBoundary";
import { SacredAdminErrorBoundary } from "@/components/sacred-admin/SacredAdminErrorBoundary";
import { installAcharyaUiErrorLogging } from "@/lib/acharya/acharya-ui-error-log";
import { AcharyaTest } from "@/components/acharya/AcharyaTest";
import { GitaHub } from "@/components/gitas/GitaHub";
import { GitaPage } from "@/components/gitas/GitaPage";
import { MarkandeyaPuranamPage } from "@/pages/library/MarkandeyaPuranamPage";
import { PURANA_TILES } from "@/lib/puranas";
import { GlobalChantingEntry } from "@/components/chanting/GlobalChantingEntry";
import { LoginPage } from "@/pages/identity/LoginPage";
import { SignupPage } from "@/pages/identity/SignupPage";
import { AuthCallbackPage } from "@/pages/identity/AuthCallbackPage";
import { ForgotPasswordPage } from "@/pages/identity/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/identity/ResetPasswordPage";
import { VerifyEmailPage } from "@/pages/identity/VerifyEmailPage";
import { AccountSettingsPage } from "@/pages/identity/AccountSettingsPage";
import { SecuritySettingsPage } from "@/pages/identity/SecuritySettingsPage";
import { ContactAcharyaPage } from "@/pages/identity/ContactAcharyaPage";
import { AuthProvider, useAuthContext } from "@/lib/auth/AuthContext";
import { RequireAuth, RequireAdmin } from "@/lib/auth/RouteGuards";
import { canAccessAdminRoutes } from "@/lib/auth/roles";
import { LegalPage } from "@/pages/identity/LegalPage";
import { GlobalChantingPage } from "@/components/chanting/GlobalChantingEntry";
import { CommunityHallPage } from "@/components/chanting/CommunityHall";
import { GlobalChantingRoom, DhyanaMandirChantingOverlay } from "@/components/chanting/GlobalChantingRoom";
import { TownhallPage } from "@/components/townhall/TownhallPage";
import { TownhallChantView } from "@/components/townhall/TownhallChantView";
import { TownhallAdminEditor } from "@/components/townhall/TownhallAdminEditor";
import { renderMantraText } from "@/lib/audio/renderMantraText";
import {
  TriLanguageBlock,
  MantraTriLanguageBlock,
  TriLanguageIfSanskrit,
} from "@/components/i18n/TriLanguageBlock";
import { toTriFromFields, verseCanonicalToTri, mantraToTriItem, storyToTriItem, guruToTriItem, asanaToTriItem } from "@/lib/i18n/tri-language";
import {
  StoryCard,
  GuruCard,
  KnowledgeBaseCard,
  SadhanaStep,
  YogaAsanaCard,
} from "@/components/audio/AiVoiceCards";
import {
  libraryLinkForFestival,
  libraryLinkForFestivalName,
  libraryLinkForVrata,
  libraryLinkForVrataName,
  libraryImageForFestival,
  libraryImageForVrata,
} from "@/lib/sacred-library/utils/panchanga-links";
import { LibraryTopicLink } from "@/components/sacred-library/LibraryTopicLink";
import { FestivalImage } from "@/components/sacred-library/FestivalImage";
import { SacredImage } from "@/components/sacred-library/SacredImage";
import { 
  Shield, Heart, Compass, Flame, Hammer, Sword, Crown, ShieldCheck, 
  HandHeart, Palette, Zap, Sparkles, BookOpen, Eye, AlertTriangle,
  HelpCircle, Link as LinkIcon, Sofa, Users, Shuffle, Cloud,
  Target, Sun, Sprout, Menu, X, User, LogOut, MessageCircle, Home,
  ChevronRight, Send, ArrowLeft, Loader2, Calendar, Moon, Star,
  Share2, Download, Copy, Smartphone, QrCode, ExternalLink,
  Wind, Volume2, Timer, Play, Pause, Square, CheckCircle,
  Brain, CalendarDays, PenLine, Activity, LifeBuoy
} from "lucide-react";

import { getApiBaseUrl } from "@/lib/api-base";
import { withSkipGlobalError } from "@/lib/http/skip-global-error";

const API = getApiBaseUrl();

async function trackJourneyEvent(token, kind, resourceId, title) {
  if (!token || !resourceId) return;
  try {
    await axios.post(
      `${API}/user/events/track`,
      { kind, resource_id: resourceId, title },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (_) {
    /* non-blocking */
  }
}

function prayerAudioSrc(session) {
  const stream = session?.audio?.stream_url || session?.guidance?.audio?.stream_url;
  if (stream) return `${API}${stream.startsWith("/") ? stream : `/${stream}`}`;
  return session?.audio?.url || session?.guidance?.audio?.url || "";
}
// redeploy trigger
// Icon mapping
const iconMap = {
  Shield, Heart, Compass, Flame, Hammer, Sword, Crown, ShieldCheck,
  HandHeart, Palette, Zap, Sparkles, BookOpen, Eye, AlertTriangle,
  HelpCircle, Link: LinkIcon, Sofa, Users, Shuffle, Cloud, Target, Sun, Sprout,
  Swords: Sword, Cross: X, Gavel: Hammer, Calendar, Moon, Star, Volume2
};

const getIcon = (iconName) => iconMap[iconName] || Compass;

function useAppAuth() {
  const auth = useAuthContext();
  const updateProfile = useCallback(
    async (patch) => {
      if (!auth.token) throw new Error("Not signed in");
      const response = await axios.post(`${API}/user/update`, patch, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      auth.setUser(response.data);
      return response.data;
    },
    [auth.token, auth.setUser]
  );
  return {
    ...auth,
    logout: auth.signOut,
    updateProfile,
  };
}

// Logo Component
const VedaPathLogo = ({ size = "md" }) => {
  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-12 w-12",
    lg: "h-24 w-24",
    xl: "h-32 w-32"
  };
  
  return (
    <img 
      src="/vedapath-logo.png" 
      alt="VedaPath" 
      className={`${sizeClasses[size]} rounded-full object-cover`}
    />
  );
};

function GlobalErrorBanner() {
  const { globalError, clearError, t } = useI18n();
  if (!globalError) return null;
  return (
    <div className="fixed top-16 left-0 right-0 z-40 px-4" data-testid="global-error-banner">
      <Card className="max-w-2xl mx-auto bg-amber-50/95 border-amber-200/80 shadow-md">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
          <div className="flex-1">
            <p className="font-medium text-foreground">{globalError.title}</p>
            <p className="text-sm text-muted-foreground mt-1">{globalError.detail}</p>
            {globalError.offline_guidance?.length > 0 && (
              <ul className="text-xs text-muted-foreground mt-2 list-disc list-inside">
                {globalError.offline_guidance.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" onClick={() => clearError()}>
              {t("btn.continue")}
            </Button>
            <Button size="sm" onClick={() => { clearError(); window.location.reload(); }}>
              {globalError.retry || t("btn.retry")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CrisisModeModal({ open, onClose }) {
  const [help, setHelp] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/crisis/quick-help`).then((r) => setHelp(r.data)).catch(() => {});
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <Card className="max-w-lg w-full max-h-[90vh] overflow-auto bg-white" onClick={(e) => e.stopPropagation()} data-testid="crisis-modal">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-primary" />
            I need help now
          </CardTitle>
          <p className="text-xs text-amber-800">{help?.disclaimer}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {help?.grounding_practices?.map((p) => (
            <div key={p.id} className="border rounded-lg p-3">
              <p className="font-medium text-sm">{p.title}</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                {p.steps.map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
          ))}
          {help?.dharma_clarity_prompts && (
            <div>
              <p className="text-sm font-medium mb-2">Dharma clarity</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {help.dharma_clarity_prompts.map((q, i) => (
                  <li key={i}>• {q}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => { onClose(); navigate("/prayer-hall?mode=guided_breath"); }}>Breath reset</Button>
            <Button size="sm" variant="outline" onClick={() => { onClose(); navigate("/guide"); }}>Āchārya AI</Button>
            <Button size="sm" variant="outline" onClick={() => { onClose(); navigate("/crisis"); }}>Full crisis guidance</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const Navigation = ({ user, logout }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [crisisOpen, setCrisisOpen] = useState(false);
  const navigate = useNavigate();
  const { t, lang, setLang, supportedLangs } = useI18n();

  return (
    <>
    <CrisisModeModal open={crisisOpen} onClose={() => setCrisisOpen(false)} />
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <VedaPathLogo />
            <span className="font-heading text-xl font-semibold text-primary">VedaPath</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/knowledge" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-knowledge">
              {t("nav.knowledge")}
            </Link>
            <Link to="/library" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-library">
              {t("nav.library")}
            </Link>
            <Link to="/panchanga" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-panchanga">
              {t("nav.panchanga")}
            </Link>
            <Link to="/prayer-hall" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-prayer-hall">
              {t("nav.prayer_hall")}
            </Link>
            <Link to="/satsanga" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-satsanga">
              {t("nav.satsanga")}
            </Link>
            <Link to="/sadhana" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-sadhana">
              {t("nav.sadhana")}
            </Link>
            <Link to="/crisis" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-crisis">
              {t("nav.crisis")}
            </Link>
            <Link to="/diagnostic" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-diagnostic">
              {t("nav.diagnostic")}
            </Link>
            <Link to="/guide" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-guide">
              {t("nav.acharya")}
            </Link>
            {user && (
              <Link to="/journal" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-journal">
                Journal
              </Link>
            )}
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-900"
              onClick={() => setCrisisOpen(true)}
              data-testid="nav-crisis-help"
            >
              <LifeBuoy className="w-4 h-4 mr-1" />
              Help now
            </Button>
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="text-sm border border-border/80 rounded-md px-2 py-1 bg-white/70"
              aria-label={t("nav.language")}
              data-testid="nav-language"
            >
              {supportedLangs.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            {user ? (
              <>
                <Link to="/dashboard" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-dashboard">
                  {t("nav.dashboard")}
                </Link>
                {(canAccessAdminRoutes(user.role) || user.role === "teacher") && (
                  <>
                    {user.role === "teacher" && (
                      <Link to="/teacher" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-teacher">
                        Teacher
                      </Link>
                    )}
                    <Link to="/admin" className="text-foreground/80 hover:text-primary transition-colors" data-testid="nav-admin">
                      Admin
                    </Link>
                  </>
                )}
                <Button variant="ghost" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </Button>
              </>
            ) : (
              <Button onClick={() => navigate("/login")} data-testid="nav-signin">
                {t("nav.sign_in")}
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-mobile-toggle">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {menuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-border/50">
            <Link to="/knowledge" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Knowledge Base
            </Link>
            <Link to="/library" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Sacred Library
            </Link>
            <Link to="/panchanga" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Pañchāṅga
            </Link>
            <Link to="/prayer-hall" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Prayer Hall
            </Link>
            <Link to="/satsanga" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Satsaṅga
            </Link>
            <Link to="/sadhana" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Sādhana
            </Link>
            <Link to="/foundations" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Foundations
            </Link>
            <Link to="/lifestyle" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Lifestyle
            </Link>
            <Link to="/streams" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Streams
            </Link>
            <Link to="/parampara" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Paramparā
            </Link>
            <Link to="/diagnostic" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
              Diagnostic
            </Link>
            <Link to="/guide" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)} title="Your digital Vedic teacher — ask questions and receive scriptural guidance.">
              Āchārya AI
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" className="block py-2 text-foreground/80 hover:text-primary" onClick={() => setMenuOpen(false)}>
                  Dashboard
                </Link>
                <button className="block py-2 text-foreground/80 hover:text-primary" onClick={() => { logout(); navigate("/"); setMenuOpen(false); }}>
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="block py-2 text-primary font-medium" onClick={() => setMenuOpen(false)}>
                Sign In
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

// Landing Page
const FEATURE_ICONS = { User, Shield, Target, Compass, Calendar, MessageCircle };

/** Default routes for home feature cards (CMS may override with feature.link). */
const FEATURE_LINKS_BY_TITLE = {
  "14 Archetypes": "/knowledge",
  "12 Karmic Gates": "/knowledge?section=gates",
  "4 Adhikāra Levels": "/knowledge?section=adhikara",
  "4 Pathways": "/knowledge?section=pathways",
  "Pañchāṅga": "/panchanga",
  "Dharma Āchārya AI": "/guide",
};

function resolveFeatureLink(feature) {
  return feature?.link || feature?.href || FEATURE_LINKS_BY_TITLE[feature?.title] || null;
}

const LandingPageContent = ({ user }) => {
  const navigate = useNavigate();
  const { page, loading } = useCmsPage();
  const L = useCmsLabels();
  const features = cmsJson(page, "features", []);
  const heroBg = cmsText(page, "hero_image_url", "https://images.unsplash.com/photo-1617634667039-8e4cb277ab46?w=1920");
  const [featuredStory, setFeaturedStory] = useState(null);
  const [featuredVerse, setFeaturedVerse] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/content/stories`, withSkipGlobalError()).catch(() => ({ data: [] })),
      axios.get(`${API}/content/verses`, withSkipGlobalError()).catch(() => ({ data: [] })),
    ]).then(([storiesRes, versesRes]) => {
      const stories = Array.isArray(storiesRes.data) ? storiesRes.data : [];
      const verses = Array.isArray(versesRes.data) ? versesRes.data : [];
      if (stories[0]) setFeaturedStory(stories[0]);
      if (verses[0]) setFeaturedVerse(verses[0]);
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen noise">
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url('${heroBg}')` }} />
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-accent/30" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center py-24">
          <div className="flex justify-center mb-8 animate-float">
            <div className="w-32 h-32 rounded-full shadow-float border-4 border-secondary/30 overflow-hidden">
              <VedaPathLogo size="xl" />
            </div>
          </div>
          <h1 className="font-heading text-5xl md:text-7xl font-semibold tracking-tight leading-[1.1] text-foreground mb-6">
            {L.heroTitle} <span className="text-primary">{L.heroHighlight}</span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto mb-4 font-body leading-relaxed">{L.heroSubtitle}</p>
          <p className="text-lg text-muted-foreground/80 max-w-xl mx-auto mb-10">{L.heroBody}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/diagnostic")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg font-medium shadow-soft transition-all hover:-translate-y-0.5"
              data-testid="hero-start-journey"
            >
              {L.ctaPrimary} <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/knowledge")}
              className="border-secondary/30 hover:bg-secondary/10 rounded-full px-8 py-6 text-lg"
              data-testid="hero-explore"
            >
              {L.ctaSecondary}
            </Button>
          </div>
        </div>
      </section>
      {(featuredStory || featuredVerse) && (
        <section className="py-16 px-6 border-t border-border/40" data-testid="home-featured-sacred">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            {featuredStory && (
              <Card className="bg-white/85 shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Featured Story</CardTitle>
                  <CardDescription>{featuredStory.canonical?.title}</CardDescription>
                </CardHeader>
                <CardContent>
                  <TriLanguageBlock item={storyToTriItem(featuredStory)} meaningPrefix="summary" meaningAudioMode="story" compact />
                  <Button variant="link" className="px-0 mt-3" onClick={() => navigate("/scriptures")}>
                    Explore scriptures
                  </Button>
                </CardContent>
              </Card>
            )}
            {featuredVerse && (
              <Card className="bg-white/85 shadow-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Featured Gītā Verse</CardTitle>
                  <CardDescription>{featuredVerse.canonical?.scriptural_reference || featuredVerse.canonical?.title}</CardDescription>
                </CardHeader>
                <CardContent>
                  <TriLanguageBlock item={verseCanonicalToTri(featuredVerse)} meaningPrefix="translation" compact />
                  <Button variant="link" className="px-0 mt-3" onClick={() => navigate("/gita")}>
                    Open Gītā hub
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-4xl md:text-5xl font-medium text-center mb-4">{L.featuresTitle}</h2>
          <p className="text-center text-muted-foreground text-lg mb-16 max-w-2xl mx-auto">{L.featuresSubtitle}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => {
              const Icon = FEATURE_ICONS[feature.icon] || User;
              const href = resolveFeatureLink(feature);
              const card = (
                <Card
                  className={`bg-white/80 backdrop-blur-sm border border-border/60 rounded-xl p-8 shadow-card transition-all duration-300 group h-full ${
                    href ? "hover:shadow-float cursor-pointer hover:-translate-y-0.5" : ""
                  }`}
                  data-testid={href ? `home-feature-${i}` : undefined}
                >
                  <Icon className={`w-10 h-10 ${feature.color} mb-4 group-hover:scale-110 transition-transform`} />
                  <h3 className="font-heading text-xl font-medium mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.desc}</p>
                  {href && (
                    <p className="text-xs text-primary mt-4 flex items-center gap-1 opacity-80 group-hover:opacity-100">
                      Explore <ChevronRight className="w-3 h-3" />
                    </p>
                  )}
                </Card>
              );
              return href ? (
                <Link key={i} to={href} className="block no-underline text-inherit">
                  {card}
                </Link>
              ) : (
                <div key={i}>{card}</div>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-24 px-6 bg-gradient-to-br from-primary/5 to-secondary/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-medium mb-6">{L.ctaBlockTitle}</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">{L.ctaBlockBody}</p>
          <Button
            onClick={() => navigate(user ? "/diagnostic" : "/login")}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-medium shadow-soft"
            data-testid="cta-start"
          >
            {user ? L.ctaDiagnostic : L.ctaSignup}
          </Button>
        </div>
      </section>
      <MobileAppBanner />
    </div>
  );
};

const LandingPage = ({ user }) => (
  <CmsPageShell slug="home" layout="embedded">
    <LandingPageContent user={user} />
  </CmsPageShell>
);

function GlobalFooter() {
  return (
    <footer
      className="border-t border-border/60 bg-background/80 py-6 px-4 text-center text-sm text-muted-foreground"
      data-testid="global-footer"
    >
      <p>© 2026 VedaPath. All Rights Reserved.</p>
      <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-3 text-xs">
        <Link to="/terms" className="hover:text-primary">
          Terms of Use
        </Link>
        <Link to="/privacy" className="hover:text-primary">
          Privacy Policy
        </Link>
        <Link to="/copyright" className="hover:text-primary">
          Copyright
        </Link>
        <Link to="/dmca" className="hover:text-primary">
          DMCA
        </Link>
        <Link to="/contact-acharya" className="hover:text-primary">
          Contact Acharya
        </Link>
      </nav>
    </footer>
  );
}

function EmailVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage("Missing verification token.");
      return;
    }
    axios
      .get(`${API}/auth/verify`, { params: { token } })
      .then((res) => {
        setStatus("success");
        setMessage(res.data?.message || "Email verified.");
        setTimeout(() => navigate("/login?verified=1"), 2000);
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          err.response?.data?.error ||
            err.response?.data?.detail ||
            "Verification failed."
        );
      });
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>
            {status === "loading"
              ? "Verifying email…"
              : status === "success"
                ? "Email verified"
                : "Verification failed"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status === "loading" ? (
            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
          ) : (
            <p className="text-muted-foreground">{message}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Auth Page (login, register, forgot password, reset password)
const AuthPage = ({ login, register }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("seeker");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpChannel, setOtpChannel] = useState("email");
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => {
      setResendCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    const t = searchParams.get("token");
    if (location.pathname === "/auth/reset" || (t && location.pathname !== "/auth/verify")) {
      setMode("reset");
      if (t) setResetToken(t);
    }
    if (searchParams.get("verified") === "1") {
      setMode("login");
      setInfo("Email verified. You can sign in.");
    }
  }, [location.pathname, searchParams]);

  const sendOtp = async () => {
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const payload =
        otpChannel === "whatsapp"
          ? { phone, channel: "whatsapp" }
          : { email, channel: "email" };
      const res = await axios.post(`${API}/auth/send-otp`, payload);
      setOtpSent(true);
      setOtpVerified(false);
      setOtpCode("");
      setResendCooldown(60);
      if (res.data?.verified === true) {
        setOtpVerified(true);
        setInfo(res.data?.message || "Verification skipped (optional). You may continue.");
        return;
      }
      if (res.data.sent === false) {
        setInfo(
          res.data.reason
            ? `Code generated but delivery pending: ${res.data.reason}`
            : "Code generated. Check your messages (delivery may be pending in dev)."
        );
      } else {
        setInfo(
          otpChannel === "whatsapp"
            ? "Verification code sent via WhatsApp."
            : "Verification code sent to your email."
        );
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to send OTP");
    }
    setLoading(false);
  };

  const verifyOtp = async () => {
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const payload =
        otpChannel === "whatsapp"
          ? { phone, otp: otpCode.trim() }
          : { email, otp: otpCode.trim() };
      await axios.post(`${API}/auth/verify-otp`, payload);
      setOtpVerified(true);
      setInfo("Verified successfully. You may continue to sign in or register.");
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Verification failed");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "otp") {
        if (otpVerified) {
          setMode("login");
          setInfo("");
        } else if (!otpSent) {
          await sendOtp();
          setLoading(false);
          return;
        } else {
          await verifyOtp();
          setLoading(false);
          return;
        }
      } else if (mode === "login") {
        await login(email, password);
        navigate("/dashboard");
      } else if (mode === "register") {
        const res = await register(email, password, name, role);
        if (res.access_token) {
          navigate("/dashboard");
        } else {
          let msg =
            res.message ||
            "Account created. Check your email for a verification link before signing in.";
          if (res.verify_url) msg += ` Dev link: ${res.verify_url}`;
          setInfo(msg);
          setMode("login");
        }
      } else if (mode === "forgot") {
        const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
        let msg = res.data.message || "If that email exists, a reset link has been sent.";
        if (res.data.email_sent === false && res.data.email_reason) {
          msg += ` (Email not sent: ${res.data.email_reason}. Use dev reset link if shown.)`;
        }
        if (res.data.reset_url) {
          msg += ` Reset: ${res.data.reset_url}`;
        } else if (res.data.reset_path) {
          msg += ` Dev link: ${window.location.origin}${res.data.reset_path}`;
        }
        setInfo(msg);
      } else if (mode === "reset") {
        if (!resetToken) {
          setError("Missing reset token. Open the link from your email.");
        } else if (newPassword.length < 6) {
          setError("Password must be at least 6 characters.");
        } else if (newPassword !== confirmPassword) {
          setError("Passwords do not match.");
        } else {
          await axios.post(`${API}/auth/reset-password`, {
            token: resetToken,
            password: newPassword,
          });
          setInfo("Password updated. You can sign in.");
          setMode("login");
          setPassword("");
          setNewPassword("");
          setConfirmPassword("");
        }
      }
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        err.message ||
        "An error occurred";
      setError(msg);
    }
    setLoading(false);
  };

  const title =
    mode === "login"
      ? "Welcome Back"
      : mode === "register"
        ? "Begin Your Journey"
        : mode === "forgot"
          ? "Forgot Password"
          : mode === "otp"
            ? otpVerified
              ? "Verified"
              : otpSent
                ? "Enter OTP"
                : "Verify with OTP"
            : "Set New Password";

  const description =
    mode === "login"
      ? "Sign in to continue your path (verification optional — required only for sādhaka tracking)."
      : mode === "register"
        ? "Create an account to save your progress"
        : mode === "forgot"
          ? "We will email a reset link when you submit your address."
          : mode === "otp"
            ? otpVerified
              ? "Your email or phone is verified."
              : otpSent
                ? "Enter the 6-digit code we sent you."
                : "Receive a one-time code by email or WhatsApp."
            : "Choose a new password for your account.";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24 noise">
      <Card className="w-full max-w-md bg-white/90 backdrop-blur-sm shadow-float" data-testid="auth-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <VedaPathLogo />
          </div>
          <CardTitle className="font-heading text-2xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="auth-name"
                  className="bg-white/50 border-border/80 focus:border-secondary"
                />
              </div>
            )}
            {mode === "otp" && !otpSent && (
              <>
                <div className="flex gap-2" role="group">
                  <Button type="button" variant={otpChannel === "email" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setOtpChannel("email")}>Email OTP</Button>
                  <Button type="button" variant={otpChannel === "whatsapp" ? "default" : "outline"} size="sm" className="flex-1" onClick={() => setOtpChannel("whatsapp")}>WhatsApp OTP</Button>
                </div>
                {otpChannel === "email" ? (
                  <div>
                    <Label htmlFor="otp-email">Email</Label>
                    <Input id="otp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="otp-email" className="bg-white/50 border-border/80 focus:border-secondary" />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="otp-phone">Phone (with country code)</Label>
                    <Input id="otp-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91XXXXXXXXXX" required data-testid="otp-phone" className="bg-white/50 border-border/80 focus:border-secondary" />
                  </div>
                )}
              </>
            )}
            {mode === "otp" && otpSent && !otpVerified && (
              <div>
                <Label htmlFor="otp-code">Verification code</Label>
                <Input id="otp-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required data-testid="otp-code" className="bg-white/50 border-border/80 focus:border-secondary text-center text-lg tracking-widest" placeholder="000000" />
                <div className="flex justify-between items-center mt-2">
                  <button type="button" disabled={resendCooldown > 0 || loading} onClick={sendOtp} className="text-sm text-primary hover:underline disabled:opacity-50" data-testid="otp-resend">{resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}</button>
                  <button type="button" onClick={() => { setOtpSent(false); setOtpCode(""); setError(""); setInfo(""); }} className="text-sm text-muted-foreground hover:underline">Change {otpChannel === "email" ? "email" : "phone"}</button>
                </div>
              </div>
            )}
            {mode === "otp" && otpVerified && (
              <div className="text-center py-4" data-testid="otp-success">
                <CheckCircle className="w-12 h-12 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{otpChannel === "email" ? email : phone} is verified.</p>
              </div>
            )}
            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required={mode !== "reset"}
                  data-testid="auth-email"
                  className="bg-white/50 border-border/80 focus:border-secondary"
                />
              </div>
            )}
            {(mode === "login" || mode === "register") && (
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="auth-password"
                  className="bg-white/50 border-border/80 focus:border-secondary"
                />
              </div>
            )}
            {mode === "reset" && (
              <>
                <div>
                  <Label htmlFor="new-password">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="auth-new-password"
                    className="bg-white/50 border-border/80 focus:border-secondary"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-white/50 border-border/80 focus:border-secondary"
                  />
                </div>
              </>
            )}
            {mode === "register" && (
              <div>
                <Label htmlFor="role">I am a</Label>
                <select
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-border/80 bg-white/50 focus:border-secondary focus:outline-none"
                  data-testid="auth-role"
                >
                  <option value="seeker">Seeker</option>
                  <option value="teacher">Teacher</option>
                </select>
              </div>
            )}
            {error && <p className="text-destructive text-sm">{error}</p>}
            {info && <p className="text-sm text-muted-foreground">{info}</p>}
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="auth-submit">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "login" ? (
                "Sign In"
              ) : mode === "register" ? (
                "Create Account"
              ) : mode === "forgot" ? (
                "Send reset link"
              ) : mode === "otp" ? (
                otpVerified ? "Continue to sign in" : otpSent ? "Verify code" : "Send code"
              ) : (
                "Update password"
              )}
            </Button>
          </form>
          <div className="mt-4 text-center space-y-2">
            {mode === "login" && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setMode("forgot");
                    setError("");
                    setInfo("");
                  }}
                  className="block w-full text-primary hover:underline text-sm"
                  data-testid="auth-forgot"
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  onClick={() => setMode("register")}
                  className="text-primary hover:underline text-sm"
                  data-testid="auth-toggle"
                >
                  Need an account? Register
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("otp");
                    setOtpSent(false);
                    setOtpVerified(false);
                    setOtpCode("");
                    setError("");
                    setInfo("");
                  }}
                  className="block w-full text-primary hover:underline text-sm"
                  data-testid="auth-otp"
                >
                  Verify with Email or WhatsApp OTP
                </button>
              </>
            )}
            {mode === "otp" && (
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setOtpSent(false);
                  setOtpVerified(false);
                  setOtpCode("");
                  setError("");
                  setInfo("");
                }}
                className="text-primary hover:underline text-sm"
              >
                Back to sign in
              </button>
            )}
            {mode === "register" && (
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-primary hover:underline text-sm"
                data-testid="auth-toggle"
              >
                Already have an account? Sign In
              </button>
            )}
            {mode === "forgot" && (
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setError("");
                  setInfo("");
                }}
                className="text-primary hover:underline text-sm"
              >
                Back to sign in
              </button>
            )}
            {mode === "reset" && (
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-primary hover:underline text-sm"
              >
                Back to sign in
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Diagnostic Page
const DiagnosticPage = ({ token, setUser }) => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get(`${API}/diagnostic/questions`);
        setQuestions(response.data.questions || response.data);
      } catch (err) {
        console.error("Failed to fetch questions:", err);
      }
      setLoading(false);
    };
    fetchQuestions();
  }, []);

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const answersArray = Object.entries(answers).map(([question_id, answer]) => ({
      question_id,
      answer,
      score: 1
    }));

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/diagnostic/submit`, { answers: answersArray }, { headers });
      setResult(response.data);
      if (token && setUser) {
        const userResponse = await axios.get(`${API}/user`, { headers });
        setUser(userResponse.data);
      }
    } catch (err) {
      console.error("Failed to submit diagnostic:", err);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (result) {
    return <DiagnosticResult result={result} navigate={navigate} />;
  }

  const progress = (Object.keys(answers).length / questions.length) * 100;
  const question = questions[currentQuestion];

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="diagnostic-page">
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-10 -z-10"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1621847468516-1ed5d0df56fe?w=1920')" }}
      />
      
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-medium mb-2">Dharma Diagnostic</h1>
          <p className="text-muted-foreground">Discover your spiritual archetype and path</p>
        </div>

        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {question && (
          <Card className="bg-white/90 backdrop-blur-sm shadow-float animate-in" data-testid="diagnostic-question-card">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-center">{question.question}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(question.id, option.value)}
                  className={`w-full p-4 rounded-xl border text-left transition-all hover:border-primary hover:bg-primary/5 ${
                    answers[question.id] === option.value 
                      ? "border-primary bg-primary/10" 
                      : "border-border/60 bg-white/50"
                  }`}
                  data-testid={`diagnostic-option-${option.value}`}
                >
                  <span className="font-medium">{option.text}</span>
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between mt-6">
          <Button 
            variant="outline" 
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
            data-testid="diagnostic-prev"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Previous
          </Button>
          
          {Object.keys(answers).length === questions.length && (
            <Button onClick={handleSubmit} disabled={submitting} data-testid="diagnostic-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Complete Diagnostic
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Diagnostic Result Component
const DiagnosticResult = ({ result, navigate }) => {
  const [archetype, setArchetype] = useState(null);

  useEffect(() => {
    if (result.archetype) {
      setArchetype(result.archetype);
      return;
    }
    const fetchArchetype = async () => {
      try {
        const response = await axios.get(`${API}/archetypes/${result.archetype_id}`);
        setArchetype(response.data);
      } catch (err) {
        console.error("Failed to fetch archetype:", err);
      }
    };
    fetchArchetype();
  }, [result]);

  if (!archetype) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const Icon = getIcon(archetype.icon);

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="diagnostic-result">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6" style={{ backgroundColor: archetype.color + '20' }}>
            <Icon className="w-12 h-12" style={{ color: archetype.color }} />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
            You are the <span style={{ color: archetype.color }}>{archetype.name}</span>
          </h1>
          <p className="text-xl text-muted-foreground italic">"{archetype.essence}"</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white/90 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Core Attributes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Core Need</p>
                <p className="font-medium">{archetype.core_need}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Primary Fear</p>
                <p className="font-medium">{archetype.primary_fear}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Strengths</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {archetype.core_strengths.map((s, i) => (
                    <Badge key={i} variant="secondary">{s}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/90 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Your Path</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Adhikāra Level</p>
                <Badge className="mt-1 capitalize">{result.adhikara_level}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recommended Pathways</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {result.pathway_recommendation.map((p, i) => (
                    <Badge key={i} variant="outline" className="capitalize">{p} Yoga</Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Practice Intensity</p>
                <p className="font-medium">{result.practice_intensity}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white/90 shadow-card mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Karmic Gates to Work Through</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
                <p className="text-sm text-muted-foreground mb-1">Primary Gate</p>
                <p className="font-medium capitalize">{result.primary_gate.replace("_", " ")} Gate</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 border border-border/60">
                <p className="text-sm text-muted-foreground mb-1">Secondary Gate</p>
                <p className="font-medium capitalize">{result.secondary_gate.replace("_", " ")} Gate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-primary/5 to-secondary/10 shadow-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Practice Priorities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {archetype.practice_priorities.map((p, i) => (
                <Badge key={i} className="bg-primary/10 text-primary border-primary/20">{p}</Badge>
              ))}
            </div>
            <Separator className="my-4" />
            <p className="text-sm text-muted-foreground mb-2">What to Avoid</p>
            <div className="flex flex-wrap gap-2">
              {archetype.red_flag_risks.map((r, i) => (
                <Badge key={i} variant="destructive" className="bg-destructive/10 text-destructive border-destructive/20">{r}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {result.sadhana_plan && (
          <Card className="bg-white/90 shadow-card mt-8">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Personalized Sādhana Plan</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {result.sadhana_plan.daily?.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-1">Daily</p>
                  <ul className="space-y-1">
                    {result.sadhana_plan.daily.map((d, i) => (
                      <li key={i} className="flex gap-2"><span className="text-primary">•</span>{d}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.integrations?.prayer_hall_modes?.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-1">Prayer Hall modes</p>
                  <div className="flex flex-wrap gap-2">
                    {result.integrations.prayer_hall_modes.map((m, i) => (
                      <Badge key={i} variant="outline">{m.replace(/_/g, " ")}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {result.recommendations?.length > 0 && (
          <Card className="bg-white/90 shadow-card mt-8">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Your Recommendations</CardTitle>
              {result.seeker_state && (
                <CardDescription>
                  Seeker state: <span className="capitalize">{result.seeker_state}</span>
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {result.recommendations.map((rec, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-secondary">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-center gap-4 mt-8">
          <Button variant="outline" onClick={() => navigate("/knowledge")} data-testid="result-explore">
            Explore Knowledge Base
          </Button>
          <Button onClick={() => navigate("/guide")} data-testid="result-guide" title="Personalized guidance for your spiritual path — based on your archetype and curriculum.">
            <MessageCircle className="w-4 h-4 mr-2" /> Consult Dharma Āchārya AI
          </Button>
        </div>
      </div>
    </div>
  );
};

// Knowledge Base Page
const KnowledgePageInner = ({ token }) => {
  const L = useCmsLabels();
  const [searchParams] = useSearchParams();
  const section = searchParams.get("section");
  const [activeTab, setActiveTab] = useState("archetypes");
  const [archetypes, setArchetypes] = useState([]);
  const [gates, setGates] = useState([]);
  const [adhikara, setAdhikara] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [categories, setCategories] = useState([]);
  const [topicItems, setTopicItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [pathwayDetail, setPathwayDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) trackJourneyEvent(token, "knowledge_view", "knowledge", "Knowledge");
  }, [token]);

  const openPathway = async (path) => {
    setSelectedItem(path);
    setSelectedType("pathway");
    setPathwayDetail(null);
    try {
      const res = await axios.get(`${API}/knowledge/pathway/${path.id}`);
      setPathwayDetail(res.data);
    } catch (err) {
      console.error("Failed to load pathway curriculum:", err);
    }
  };

  const loadCategory = async (catId) => {
    try {
      const res = await axios.get(`${API}/knowledge/category/${catId}`);
      setTopicItems(res.data.items || []);
      setActiveCategory(catId);
    } catch (err) {
      console.error("Failed to load category:", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [archRes, gateRes, adhRes, pathRes, currRes, catRes] = await Promise.all([
          axios.get(`${API}/archetypes?enriched=1`),
          axios.get(`${API}/gates?enriched=1`),
          axios.get(`${API}/adhikara`),
          axios.get(`${API}/pathways`),
          axios.get(`${API}/curriculum`),
          axios.get(`${API}/knowledge/categories`),
        ]);
        setArchetypes(archRes.data);
        setGates(gateRes.data);
        setAdhikara(adhRes.data);
        setPathways(pathRes.data);
        setCurriculum(currRes.data);
        setCategories(catRes.data);
        if (catRes.data?.length) loadCategory(catRes.data[0].id);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const map = { archetypes: "archetypes", gates: "gates", adhikara: "adhikara", pathways: "pathways", curriculum: "curriculum" };
    if (section && map[section]) setActiveTab(map[section]);
  }, [section]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const ItemDetail = ({ item, type }) => {
    const Icon = getIcon(item.icon || "BookOpen");
    
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setSelectedItem(null); setSelectedType(null); }}>
        <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto bg-white" onClick={(e) => e.stopPropagation()} data-testid="knowledge-detail-modal">
          <CardHeader className="border-b">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: item.color + '20' }}>
                <Icon className="w-8 h-8" style={{ color: item.color }} />
              </div>
              <div>
                <CardTitle className="font-heading text-2xl">{item.name || item.title}</CardTitle>
                {item.sanskrit && (
                  <TriLanguageIfSanskrit
                    item={toTriFromFields({ sanskrit: item.sanskrit, english: item.essence || item.summary || item.title })}
                    meaningPrefix="translation"
                    compact
                    token={token}
                  />
                )}
                {item.name && item.title && item.name !== item.title && (
                  <p className="text-muted-foreground">{item.title}</p>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="py-6 space-y-6">
            {item.essence && (
              <TriLanguageBlock
                item={toTriFromFields({ english: item.essence, translation_en: item.essence, sanskrit: item.sanskrit })}
                meaningPrefix="translation"
                token={token}
                className="text-lg italic text-primary"
              />
            )}

            {type === "topic" && (
              <div className="space-y-4">
                <TriLanguageBlock
                  item={toTriFromFields({ english: item.simple, summary_en: item.simple, sanskrit: item.sanskrit })}
                  meaningPrefix="summary"
                  token={token}
                />
                {item.moderate && (
                  <TriLanguageBlock
                    item={toTriFromFields({ english: item.moderate, translation_en: item.moderate })}
                    meaningPrefix="translation"
                    meaningAudioMode="commentary"
                    token={token}
                    englishLabel="Moderate"
                  />
                )}
                {item.elevated && (
                  <TriLanguageBlock
                    item={toTriFromFields({ english: item.elevated, commentary_en: item.elevated })}
                    meaningPrefix="commentary"
                    meaningAudioMode="sanskrit_explanation"
                    token={token}
                    englishLabel="Elevated"
                  />
                )}
                {item.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((t, i) => <Badge key={i} variant="secondary">{t}</Badge>)}
                  </div>
                )}
              </div>
            )}
            
            {type === "archetype" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Core Need</p>
                    <p className="font-medium">{item.core_need}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Primary Fear</p>
                    <p className="font-medium">{item.primary_fear}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Core Strengths</p>
                  <div className="flex flex-wrap gap-2">
                    {item.core_strengths?.map((s, i) => <Badge key={i} variant="secondary">{s}</Badge>)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Practice Priorities</p>
                  <div className="flex flex-wrap gap-2">
                    {item.practice_priorities?.map((p, i) => <Badge key={i}>{p}</Badge>)}
                  </div>
                </div>
                {item.stories?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">Stories from tradition</p>
                    <div className="space-y-3">
                      {item.stories.map((st, i) => (
                        <div key={i} className="border-l-2 border-primary/40 pl-3 text-sm">
                          <p className="font-medium">{st.title}</p>
                          {st.source && <p className="text-xs text-muted-foreground">{st.source}</p>}
                          <TriLanguageBlock
                            item={storyToTriItem({ canonical: { summary_en: st.summary, short_story: st.summary }, ...st })}
                            meaningPrefix="summary"
                            meaningAudioMode="story"
                            compact
                            token={token}
                            className="text-muted-foreground mt-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {type === "gate" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Root Fear</p>
                    <p className="font-medium">"{item.root_fear}"</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Distortion</p>
                    <p className="font-medium">{item.distortion}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Antidote</p>
                  <Badge className="mt-1">{item.antidote}</Badge>
                </div>
              </>
            )}

            {type === "pathway" && (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">Best For</p>
                  <p className="font-medium">{pathwayDetail?.who_for || item.who_for}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Core Practices</p>
                  <div className="flex flex-wrap gap-2">
                    {(pathwayDetail?.core_practices || item.core_practices)?.map((p, i) => <Badge key={i} variant="secondary">{p}</Badge>)}
                  </div>
                </div>
                {pathwayDetail?.overview && <p className="text-sm leading-relaxed mt-4">{pathwayDetail.overview}</p>}
                {pathwayDetail?.philosophy && <p className="text-sm text-muted-foreground leading-relaxed mt-2">{pathwayDetail.philosophy}</p>}
              </>
            )}

            {type === "adhikara" && (
              <>
                <div>
                  <p className="text-sm text-muted-foreground">Inner State</p>
                  <p className="font-medium">{item.inner_state}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Suitable Practices</p>
                  <div className="flex flex-wrap gap-2">
                    {item.suitable_practices?.map((p, i) => <Badge key={i} variant="secondary">{p}</Badge>)}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="knowledge-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-4">{L.pageTitle}</h1>
          <p className="text-muted-foreground text-lg">{L.pageSubtitle}</p>
        </div>

        <KnowledgeTabCms activeTab={activeTab} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap justify-center mb-8 bg-transparent gap-2">
            <TabsTrigger value="archetypes" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {L.tabArchetypes}
            </TabsTrigger>
            <TabsTrigger value="gates" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {L.tabGates}
            </TabsTrigger>
            <TabsTrigger value="adhikara" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {L.tabAdhikara}
            </TabsTrigger>
            <TabsTrigger value="pathways" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {L.tabPathways}
            </TabsTrigger>
            <TabsTrigger value="curriculum" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {L.tabCurriculum}
            </TabsTrigger>
            <TabsTrigger value="topics" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              {L.tabTopics}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="archetypes">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {archetypes.map((arch) => {
                const Icon = getIcon(arch.icon);
                return (
                  <Card 
                    key={arch.id} 
                    className="cursor-pointer hover:shadow-float transition-all bg-white/80"
                    onClick={() => { setSelectedItem(arch); setSelectedType("archetype"); }}
                    data-testid={`archetype-card-${arch.id}`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: arch.color + '20' }}>
                        <Icon className="w-8 h-8" style={{ color: arch.color }} />
                      </div>
                      <h3 className="font-heading text-lg font-medium">{arch.name}</h3>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{arch.essence}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="gates" id="knowledge-section-gates">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gates.map((gate) => {
                const Icon = getIcon(gate.icon);
                return (
                  <Card 
                    key={gate.id} 
                    className="cursor-pointer hover:shadow-float transition-all bg-white/80"
                    onClick={() => { setSelectedItem(gate); setSelectedType("gate"); }}
                    data-testid={`gate-card-${gate.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-full" style={{ backgroundColor: gate.color + '20' }}>
                          <Icon className="w-5 h-5" style={{ color: gate.color }} />
                        </div>
                        <div>
                          <h3 className="font-heading font-medium">{gate.name}</h3>
                          <TriLanguageIfSanskrit
                            item={toTriFromFields({ sanskrit: gate.sanskrit, english: gate.description })}
                            meaningPrefix="translation"
                            compact
                            token={token}
                          />
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">{gate.essence}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="adhikara">
            <div className="grid sm:grid-cols-2 gap-6">
              {adhikara.map((level) => {
                const Icon = getIcon(level.icon);
                return (
                  <Card 
                    key={level.id} 
                    className="cursor-pointer hover:shadow-float transition-all bg-white/80"
                    onClick={() => { setSelectedItem(level); setSelectedType("adhikara"); }}
                    data-testid={`adhikara-card-${level.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-full" style={{ backgroundColor: level.color + '20' }}>
                          <Icon className="w-6 h-6" style={{ color: level.color }} />
                        </div>
                        <div>
                          <h3 className="font-heading text-xl font-medium">{level.name}</h3>
                          <p className="text-muted-foreground">{level.title}</p>
                        </div>
                      </div>
                      <p className="text-sm">{level.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="pathways">
            <div className="grid sm:grid-cols-2 gap-6">
              {pathways.map((path) => {
                const Icon = getIcon(path.icon);
                return (
                  <Card 
                    key={path.id} 
                    className="cursor-pointer hover:shadow-float transition-all bg-white/80"
                    onClick={() => openPathway(path)}
                    data-testid={`pathway-card-${path.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-full" style={{ backgroundColor: path.color + '20' }}>
                          <Icon className="w-6 h-6" style={{ color: path.color }} />
                        </div>
                        <div>
                          <h3 className="font-heading text-xl font-medium">{path.name}</h3>
                          <p className="text-muted-foreground">{path.title}</p>
                        </div>
                      </div>
                      <p className="text-sm italic text-primary mb-3">"{path.essence}"</p>
                      <p className="text-sm text-muted-foreground">{path.who_for}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="curriculum">
            <div className="space-y-6">
              {curriculum.map((stage, index) => {
                const Icon = getIcon(stage.icon);
                return (
                  <Card key={stage.id} className="bg-white/80" data-testid={`curriculum-card-${stage.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center">
                          <div className="p-3 rounded-full" style={{ backgroundColor: stage.color + '20' }}>
                            <Icon className="w-6 h-6" style={{ color: stage.color }} />
                          </div>
                          {index < curriculum.length - 1 && (
                            <div className="w-0.5 h-16 bg-border mt-4" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading text-xl font-medium">{stage.name}</h3>
                          <p className="text-muted-foreground text-sm mb-3">{stage.duration}</p>
                          <p className="mb-4">{stage.purpose}</p>
                          <div className="flex flex-wrap gap-2">
                            {stage.core_practices.slice(0, 4).map((p, i) => (
                              <Badge key={i} variant="secondary">{p}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="topics">
            <div className="flex flex-wrap gap-2 mb-6 justify-center">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={activeCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => loadCategory(cat.id)}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {topicItems.map((item) => (
                <KnowledgeBaseCard
                  key={item.id}
                  item={item}
                  token={token}
                  onClick={() => { setSelectedItem(item); setSelectedType("topic"); }}
                />
              ))}
            </div>
            {topicItems.length === 0 && (
              <p className="text-center text-muted-foreground py-8">{L.emptyTopics}</p>
            )}
          </TabsContent>
        </Tabs>

        {selectedItem && selectedType && (
          <ItemDetail item={selectedItem} type={selectedType} />
        )}
      </div>
    </div>
  );
};

const KnowledgePage = ({ token }) => (
  <CmsPageShell slug="knowledge-base" layout="embedded">
    <KnowledgePageInner token={token} />
  </CmsPageShell>
);

// AI Guide Page (Acharya 3.0)
const GuidePageInner = ({ token, user }) => {
  const L = useCmsLabels();
  const [seekerState, setSeekerState] = useState("curious");

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="guide-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-2">
          <VedaPathLogo size="sm" />
        </div>
        <ErrorBoundary>
          <AcharyaChat
            token={token}
            userId={user?.id}
            userEmail={user?.email}
            seekerState={seekerState}
            onSeekerStateChange={setSeekerState}
            labels={{
              pageTitle: L.pageTitle,
              pageSubtitle: L.pageSubtitle,
              pageNote: L.pageNote,
              emptyTitle: L.emptyTitle,
              emptyBody: L.emptyBody,
              inputPlaceholder: L.inputPlaceholder,
              errorMessage: L.errorMessage,
              seekerTone: L.seekerTone,
              seekerStates: L.seekerStates,
              fallbackSuggestions: L.fallbackSuggestions,
            }}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
};

const GuidePage = ({ token, user }) => (
  <CmsPageShell slug="acharya-ai" layout="embedded">
    <GuidePageInner token={token} user={user} />
  </CmsPageShell>
);

function NotificationSettings({ token }) {
  const { t } = useI18n();
  const [prefs, setPrefs] = useState({
    daily_reminder: false,
    festival_reminders: true,
    sadhana_streak_reminders: true,
    email_enabled: false,
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/user/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPrefs((p) => ({ ...p, ...(r.data.preferences || {}) })))
      .catch(() => {});
  }, [token]);

  const save = async () => {
    if (!token) return;
    try {
      await axios.post(`${API}/user/notifications/update`, prefs, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  if (!token) return null;

  return (
    <Card className="bg-white/90 shadow-card mb-8" data-testid="notification-settings">
      <CardHeader>
        <CardTitle className="font-heading text-lg">{t("notifications.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {[
          ["daily_reminder", t("notifications.daily")],
          ["festival_reminders", t("notifications.festival")],
          ["sadhana_streak_reminders", t("notifications.streak")],
        ].map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!prefs[key]}
              onChange={(e) => setPrefs({ ...prefs, [key]: e.target.checked })}
            />
            {label}
          </label>
        ))}
        <div className="flex items-center gap-3 pt-2">
          <Button size="sm" onClick={save}>{t("btn.save")}</Button>
          {saved && <span className="text-xs text-muted-foreground">Saved.</span>}
        </div>
      </CardContent>
    </Card>
  );
}

// Dashboard Page
const DashboardPageInner = ({ user, token }) => {
  const L = useCmsLabels();
  const [recommendations, setRecommendations] = useState(null);
  const [journey, setJourney] = useState(null);
  const [dashRec, setDashRec] = useState(null);
  const [dashMind, setDashMind] = useState(null);
  const [dashPriority, setDashPriority] = useState(null);
  const [panchangaToday, setPanchangaToday] = useState(null);
  const [panchangaTomorrow, setPanchangaTomorrow] = useState(null);
  const [panchangaMonth, setPanchangaMonth] = useState(null);
  const [panchangaLoading, setPanchangaLoading] = useState(true);
  const [locSaving, setLocSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const {
    loc: panchangaLoc,
    meta: locMeta,
    loading: locLoading,
    message: locMessage,
    setMessage: setLocMessage,
    detectAuto,
    setManualLoc,
    saveToProfile,
  } = usePanchangaLocation(token, user);

  useEffect(() => {
    const fetchAll = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [recRes, journeyRes, dRec, dMind, dPri] = await Promise.all([
          axios.get(`${API}/recommendations`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/user/journey`, { headers }),
          axios.get(`${API}/dashboard/recommendations`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/dashboard/mind`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/dashboard/dharma-priority`, { headers }).catch(() => ({ data: null })),
        ]);
        setRecommendations(recRes.data);
        setJourney(journeyRes.data);
        setDashRec(dRec.data);
        setDashMind(dMind.data);
        setDashPriority(dPri.data);
      } catch (err) {
        console.error("Failed to fetch dashboard:", err);
      }
      setLoading(false);
    };
    fetchAll();
  }, [token]);

  useEffect(() => {
    if (!token || !panchangaLoc || locLoading) return;
    const lang = user?.language || "en";
    const q = locQueryString(panchangaLoc);
    const tz = panchangaLoc.tz_offset_minutes;
    const { y, m } = (() => {
      const t = todayDateStr(tz);
      const [yy, mm] = t.split("-").map(Number);
      return { y: yy, m: mm };
    })();
    const tomorrow = tomorrowDateStr(tz);

    const fetchPanchanga = async () => {
      setPanchangaLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [todayRes, tomorrowRes, monthRes] = await Promise.all([
          axios.get(`${API}/panchanga/today?lang=${encodeURIComponent(lang)}&${q}`, { headers }),
          axios.get(`${API}/panchanga/today?lang=${encodeURIComponent(lang)}&date=${tomorrow}&${q}`, {
            headers,
          }),
          axios.get(
            `${API}/panchanga/month?year=${y}&month=${m}&lang=${encodeURIComponent(lang)}&${q}`,
            { headers }
          ),
        ]);
        setPanchangaToday(todayRes.data);
        setPanchangaTomorrow(tomorrowRes.data);
        setPanchangaMonth(monthRes.data);
      } catch (err) {
        console.error("Failed to fetch hybrid panchanga:", err);
      }
      setPanchangaLoading(false);
    };
    fetchPanchanga();
  }, [token, panchangaLoc, locLoading, user?.language]);

  const handleSavePanchangaLocation = async () => {
    setLocSaving(true);
    await saveToProfile();
    setLocSaving(false);
  };

  const dharmaSummaryBlock = token ? (
    <DashboardPanchangaCard
      today={panchangaToday}
      tomorrow={panchangaTomorrow}
      monthData={panchangaMonth}
      loc={panchangaLoc}
      meta={locMeta}
      locMessage={locMessage}
      locLoading={locLoading}
      panchangaLoading={panchangaLoading}
      onDetectAuto={detectAuto}
      onSaveProfile={handleSavePanchangaLocation}
      onManualApply={setManualLoc}
      locSaving={locSaving}
    />
  ) : null;

  const dashboardV2 = token && dashRec ? (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="dashboard-v2">
      <Card className="bg-white/90 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Sun className="w-4 h-4 text-primary" /> {L.dailySadhana}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p className="font-medium">{dashRec.daily_sadhana?.title}</p>
          <p>{dashRec.daily_sadhana?.practice}</p>
          <p className="text-muted-foreground">{dashRec.daily_sadhana?.minutes} min · {dashRec.daily_sadhana?.reason}</p>
          <Button size="sm" variant="outline" onClick={() => navigate("/prayer-hall")}>{L.prayerHall}</Button>
        </CardContent>
      </Card>
      <Card className="bg-white/90 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{L.dharmaTip}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm italic text-muted-foreground">{dashRec.dharma_tip?.text}</CardContent>
      </Card>
      <Card className="bg-white/90 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><CalendarDays className="w-4 h-4" /> {L.festival}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          {panchangaToday?.festivals?.[0] ? (
            <p>{L.today} {panchangaToday.festivals[0].name_en}</p>
          ) : panchangaTomorrow?.festivals?.[0] ? (
            <p>{L.tomorrow} {panchangaTomorrow.festivals[0].name_en}</p>
          ) : (
            <p className="text-muted-foreground">{L.noFestival}</p>
          )}
        </CardContent>
      </Card>
      <Card className="bg-white/90 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4" /> {L.reading}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p>{dashRec.personalized_reading?.title}</p>
          <Button size="sm" className="mt-2" variant="outline" onClick={() => navigate(dashRec.personalized_reading?.href || "/library")}>{L.open}</Button>
        </CardContent>
      </Card>
      <Card className="bg-white/90 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{L.seekerState}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <TriLanguageIfSanskrit
            item={toTriFromFields({
              sanskrit: dashRec.seeker_state?.sanskrit,
              english: dashRec.seeker_state?.name,
            })}
            meaningPrefix="translation"
            compact
          />
          <p className="text-muted-foreground mt-1">{dashRec.seeker_state?.description}</p>
        </CardContent>
      </Card>
      <Card className="bg-white/90 shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Brain className="w-4 h-4" /> {L.mindToday}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          <p>{L.tone} <strong>{dashMind?.tone_label}</strong></p>
          {dashMind?.activities?.map((a, i) => <p key={i} className="text-muted-foreground">{a}</p>)}
        </CardContent>
      </Card>
      <Card className="bg-white/90 shadow-card md:col-span-2 lg:col-span-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4" /> {L.dharmaPriority}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm flex flex-wrap items-center justify-between gap-3">
          <p className="font-medium">{dashPriority?.priority}</p>
          <Button size="sm" onClick={() => navigate(dashPriority?.href || "/sadhana")}>{L.go}</Button>
        </CardContent>
      </Card>
    </div>
  ) : null;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p>{L.signInTitle}</p>
          <Button onClick={() => navigate("/login")} className="mt-4">{L.signIn}</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const journeyBlock = journey ? (
    <div className="space-y-6 mb-8" data-testid="dashboard-journey">
      {journey.next_steps?.length > 0 && (
        <Card className="bg-white/90 shadow-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              {L.nextSteps}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {journey.next_steps.map((step, i) => (
              <button
                key={i}
                type="button"
                onClick={() => navigate(step.href)}
                className="w-full text-left p-3 rounded-lg border border-border/60 hover:bg-accent/30 transition-colors"
              >
                <p className="font-medium text-sm">{step.label}</p>
                <p className="text-xs text-muted-foreground mt-1">{step.reason}</p>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
      {journey.continue_reading?.length > 0 && (
        <Card className="bg-white/90 shadow-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              {L.continueReading}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {journey.continue_reading.map((item, i) => (
              <Button key={i} variant="outline" size="sm" onClick={() => navigate(item.href)}>
                {item.title}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}
      {journey.timeline?.length > 0 && (
        <Card className="bg-white/90 shadow-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">{L.myJourney}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 border-l-2 border-primary/30 pl-4">
              {journey.timeline.slice(0, 12).map((ev, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-primary" />
                  <p className="text-sm font-medium">{ev.title}</p>
                  <p className="text-xs text-muted-foreground">{new Date(ev.at).toLocaleString()}</p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  ) : null;

  if (!user.diagnostic_completed || !recommendations?.archetype) {
    return (
      <div className="min-h-screen py-24 px-4" data-testid="dashboard-no-diagnostic">
        <div className="max-w-6xl mx-auto">
          {dharmaSummaryBlock}
          {dashboardV2}
          {journeyBlock}
          <TriLanguageSettings />
        </div>
        <div className="flex items-center justify-center mt-8">
          <Card className="max-w-md w-full text-center p-8 bg-white/90 shadow-float">
            <Compass className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
            <h2 className="font-heading text-2xl mb-2">{L.noDiagnosticTitle}</h2>
            <p className="text-muted-foreground mb-6">{L.noDiagnosticBody}</p>
            <Button onClick={() => navigate("/diagnostic")} className="rounded-full" data-testid="dashboard-start-diagnostic">
              {L.beginDiagnostic}
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const { archetype, pathways, adhikara_level, curriculum_stage, primary_gate, secondary_gate } = recommendations;
  const ArchIcon = getIcon(archetype.icon);

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-medium">{L.welcome.replace("{name}", user.name)}</h1>
          <p className="text-muted-foreground">{L.welcomeSubtitle}</p>
        </div>

        {dharmaSummaryBlock}
        {dashboardV2}
        {journeyBlock}

        <TriLanguageSettings />
        <NotificationSettings token={token} />
        <SacredProfileSettings token={token} />
        <JourneyDashboard token={token} />

        {journey?.state_evolution && (
          <Card className="bg-white/90 shadow-card mb-8">
            <CardHeader>
              <CardTitle className="font-heading text-lg">{L.seekerInsight}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>
                {L.suggested} <strong>{journey.suggested_state}</strong> — {journey.state_evolution.evolution_note}
              </p>
              {journey.state_evolution.regression_detected && (
                <p className="text-amber-800">{L.gentlerSeason}</p>
              )}
              <p>{L.sadhanaConsistency} {journey.state_evolution.sadhana_consistency?.score ?? 0}/100</p>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Archetype Card */}
          <Card className="lg:col-span-1 bg-gradient-to-br from-white to-accent/30 shadow-card" data-testid="dashboard-archetype">
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4" style={{ backgroundColor: archetype.color + '20' }}>
                <ArchIcon className="w-10 h-10" style={{ color: archetype.color }} />
              </div>
              <h2 className="font-heading text-2xl font-medium">{archetype.name}</h2>
              <p className="text-muted-foreground italic mt-2">"{archetype.essence}"</p>
              <Separator className="my-4" />
              <div className="text-left space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">{L.coreNeed}</p>
                  <p className="text-sm font-medium">{archetype.core_need}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{L.naturalSpiral}</p>
                  <p className="text-sm font-medium">{archetype.natural_spiral}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Stage */}
            <Card className="bg-white/90 shadow-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  {L.currentStage} {curriculum_stage.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{curriculum_stage.purpose}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium mb-2">{L.focusAreas}</p>
                    <div className="flex flex-wrap gap-2">
                      {curriculum_stage.core_practices.slice(0, 4).map((p, i) => (
                        <Badge key={i} variant="secondary">{p}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">{L.adhikaraLevel}</p>
                    <Badge style={{ backgroundColor: adhikara_level.color + '20', color: adhikara_level.color }}>
                      {adhikara_level.name}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pathways */}
            <Card className="bg-white/90 shadow-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{L.recommendedPathways}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {pathways.map((path) => {
                    const PathIcon = getIcon(path.icon);
                    return (
                      <div key={path.id} className="p-4 rounded-lg border border-border/60 bg-white/50">
                        <div className="flex items-center gap-3 mb-2">
                          <PathIcon className="w-5 h-5" style={{ color: path.color }} />
                          <span className="font-medium">{path.name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{path.essence}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Gates */}
            <Card className="bg-white/90 shadow-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{L.karmicGates}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {primary_gate && (
                    <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20">
                      <div className="flex items-center gap-2 mb-2">
                        {(() => { const GateIcon = getIcon(primary_gate.icon); return <GateIcon className="w-5 h-5" style={{ color: primary_gate.color }} />; })()}
                        <span className="font-medium">{primary_gate.name}</span>
                        <Badge variant="destructive" className="text-xs">{L.primary}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{L.antidote} {primary_gate.antidote}</p>
                    </div>
                  )}
                  {secondary_gate && (
                    <div className="p-4 rounded-lg bg-muted/50 border border-border/60">
                      <div className="flex items-center gap-2 mb-2">
                        {(() => { const GateIcon = getIcon(secondary_gate.icon); return <GateIcon className="w-5 h-5" style={{ color: secondary_gate.color }} />; })()}
                        <span className="font-medium">{secondary_gate.name}</span>
                        <Badge variant="secondary" className="text-xs">{L.secondary}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{L.antidote} {secondary_gate.antidote}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Button variant="outline" onClick={() => navigate("/guide")} data-testid="dashboard-guide" title="Personalized guidance for your spiritual path — based on your archetype and curriculum.">
            <MessageCircle className="w-4 h-4 mr-2" /> {L.consultAcharya}
          </Button>
          <Button variant="outline" onClick={() => navigate("/knowledge")} data-testid="dashboard-knowledge">
            <BookOpen className="w-4 h-4 mr-2" /> {L.knowledgeBase}
          </Button>
          <Button variant="outline" onClick={() => navigate("/diagnostic")} data-testid="dashboard-retake">
            <Compass className="w-4 h-4 mr-2" /> {L.retakeDiagnostic}
          </Button>
        </div>
      </div>
    </div>
  );
};

const DashboardPage = ({ user, token }) => (
  <CmsPageShell slug="dashboard-cards" layout="embedded">
    <DashboardPageInner user={user} token={token} />
  </CmsPageShell>
);

// Teacher Dashboard Page
const TeacherPage = ({ user, token }) => {
  const [seekers, setSeekers] = useState([]);
  const [selectedSeeker, setSelectedSeeker] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSeekers = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/teacher/seekers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSeekers(response.data);
      } catch (err) {
        console.error("Failed to fetch seekers:", err);
      }
      setLoading(false);
    };
    fetchSeekers();
  }, [token]);

  const viewSeekerDetails = async (seekerId) => {
    try {
      const response = await axios.get(`${API}/teacher/seeker/${seekerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSeeker(response.data);
    } catch (err) {
      console.error("Failed to fetch seeker details:", err);
    }
  };

  if (!user || user.role !== "teacher") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p>Teacher access required</p>
          <Button onClick={() => navigate("/")} className="mt-4">Go Home</Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="teacher-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-medium">Teacher Dashboard</h1>
          <p className="text-muted-foreground">Manage and guide your seekers</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Seekers List */}
          <Card className="lg:col-span-1 bg-white/90 shadow-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Seekers ({seekers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[60vh]">
                <div className="space-y-2">
                  {seekers.map((seeker) => {
                    const hasArchetype = seeker.archetype;
                    const ArchIcon = hasArchetype ? getIcon(seeker.archetype.icon) : User;
                    return (
                      <button
                        key={seeker.id}
                        onClick={() => viewSeekerDetails(seeker.id)}
                        className={`w-full p-3 rounded-lg text-left transition-colors ${
                          selectedSeeker?.seeker?.id === seeker.id 
                            ? "bg-primary/10 border-primary" 
                            : "bg-white/50 hover:bg-muted/50"
                        } border`}
                        data-testid={`seeker-${seeker.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: hasArchetype ? seeker.archetype.color + '20' : '#f0f0f0' }}>
                            <ArchIcon className="w-5 h-5" style={{ color: hasArchetype ? seeker.archetype.color : '#888' }} />
                          </div>
                          <div>
                            <p className="font-medium">{seeker.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {hasArchetype ? seeker.archetype.name : "No diagnostic yet"}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {seekers.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">No seekers yet</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Seeker Details */}
          <Card className="lg:col-span-2 bg-white/90 shadow-card">
            {selectedSeeker ? (
              <>
                <CardHeader>
                  <CardTitle className="font-heading text-xl">
                    {selectedSeeker.seeker.name}
                  </CardTitle>
                  <CardDescription>{selectedSeeker.seeker.email}</CardDescription>
                </CardHeader>
                <CardContent>
                  {selectedSeeker.seeker.archetype ? (
                    <div className="space-y-6">
                      {/* Archetype Info */}
                      <div className="p-4 rounded-lg bg-gradient-to-br from-white to-accent/30 border border-border/60">
                        <div className="flex items-center gap-4 mb-4">
                          {(() => { 
                            const Icon = getIcon(selectedSeeker.seeker.archetype.icon); 
                            return (
                              <div className="p-3 rounded-full" style={{ backgroundColor: selectedSeeker.seeker.archetype.color + '20' }}>
                                <Icon className="w-8 h-8" style={{ color: selectedSeeker.seeker.archetype.color }} />
                              </div>
                            );
                          })()}
                          <div>
                            <h3 className="font-heading text-xl font-medium">{selectedSeeker.seeker.archetype.name}</h3>
                            <p className="text-muted-foreground italic">"{selectedSeeker.seeker.archetype.essence}"</p>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-muted-foreground">Core Need</p>
                            <p className="font-medium">{selectedSeeker.seeker.archetype.core_need}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Primary Fear</p>
                            <p className="font-medium">{selectedSeeker.seeker.archetype.primary_fear}</p>
                          </div>
                        </div>
                      </div>

                      {/* Diagnostic Result */}
                      {selectedSeeker.diagnostic_result && (
                        <div className="space-y-4">
                          <h4 className="font-heading font-medium">Diagnostic Results</h4>
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-sm text-muted-foreground">Adhikāra Level</p>
                              <Badge className="mt-1 capitalize">{selectedSeeker.diagnostic_result.adhikara_level}</Badge>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-sm text-muted-foreground">Curriculum Stage</p>
                              <Badge className="mt-1">{selectedSeeker.diagnostic_result.curriculum_stage}</Badge>
                            </div>
                            <div className="p-3 rounded-lg bg-destructive/5">
                              <p className="text-sm text-muted-foreground">Primary Gate</p>
                              <p className="font-medium capitalize">{selectedSeeker.diagnostic_result.primary_gate.replace("_", " ")}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-sm text-muted-foreground">Secondary Gate</p>
                              <p className="font-medium capitalize">{selectedSeeker.diagnostic_result.secondary_gate.replace("_", " ")}</p>
                            </div>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground mb-2">Recommended Pathways</p>
                            <div className="flex gap-2">
                              {selectedSeeker.diagnostic_result.pathway_recommendation.map((p, i) => (
                                <Badge key={i} variant="outline" className="capitalize">{p} Yoga</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Teacher Guidance */}
                      <div className="p-4 rounded-lg border border-secondary/30 bg-secondary/5">
                        <h4 className="font-heading font-medium mb-3">Teacher Guidelines</h4>
                        <div className="space-y-2">
                          <p className="text-sm"><strong>Practice Priorities:</strong></p>
                          <div className="flex flex-wrap gap-2">
                            {selectedSeeker.seeker.archetype.practice_priorities.map((p, i) => (
                              <Badge key={i} variant="secondary">{p}</Badge>
                            ))}
                          </div>
                          <p className="text-sm mt-3"><strong>Red Flags to Watch:</strong></p>
                          <div className="flex flex-wrap gap-2">
                            {selectedSeeker.seeker.archetype.red_flag_risks.map((r, i) => (
                              <Badge key={i} variant="destructive" className="bg-destructive/10 text-destructive">{r}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <User className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-muted-foreground">This seeker hasn't completed the diagnostic yet.</p>
                    </div>
                  )}
                </CardContent>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-full py-24">
                <div className="text-center">
                  <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">Select a seeker to view their details</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

// ==================== PHASE 2: SACRED LIBRARY PAGE ====================

// Layer Selector Component
const LayerSelector = ({ activeLayer, setActiveLayer }) => (
  <div className="flex gap-2 mb-6">
    {[
      { id: "simple", label: "Simple", desc: "Universal, child-friendly" },
      { id: "moderate", label: "Moderate", desc: "Devotional, adult tone" },
      { id: "elevated", label: "Elevated", desc: "Traditional, classical" }
    ].map((layer) => (
      <button
        key={layer.id}
        onClick={() => setActiveLayer(layer.id)}
        className={`flex-1 p-3 rounded-xl border text-center transition-all ${
          activeLayer === layer.id
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-white/50 border-border/60 hover:border-primary/50"
        }`}
        data-testid={`layer-${layer.id}`}
      >
        <p className="font-medium text-sm">{layer.label}</p>
        <p className="text-xs opacity-70">{layer.desc}</p>
      </button>
    ))}
  </div>
);

// Verse Display Component
const VerseDisplay = ({ verse, activeLayer, token }) => {
  const layer = verse.layers?.[activeLayer];
  
  return (
    <div className="space-y-6">
      <div className="p-6 rounded-xl bg-gradient-to-br from-primary/5 to-secondary/10 border border-primary/10">
        <TriLanguageBlock item={verseCanonicalToTri(verse)} meaningPrefix="translation" token={token} />
      </div>

      {/* Layer-specific Content */}
      {layer && (
        <Card className="bg-white/80 border-secondary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-secondary" />
              {activeLayer === "simple" ? "Simple Understanding" : 
               activeLayer === "moderate" ? "Deeper Meaning" : "Traditional Wisdom"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeLayer === "simple" && (
              <>
                <div>
                  <p className="font-medium">{layer.meaning}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Reflection</p>
                  <p>{layer.reflection}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/10">
                  <p className="text-sm text-muted-foreground">Today's Action</p>
                  <p>{layer.application}</p>
                </div>
              </>
            )}
            {activeLayer === "moderate" && (
              <>
                <p>{layer.explanation}</p>
                <div className="p-3 rounded-lg bg-primary/5">
                  <p className="text-sm text-muted-foreground">Heart Connection</p>
                  <p className="italic">{layer.emotional_resonance}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Practical Reflection</p>
                  <p>{layer.practical_reflection}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/10">
                  <p className="text-sm text-muted-foreground">Daily Application</p>
                  <p>{layer.daily_application}</p>
                </div>
              </>
            )}
            {activeLayer === "elevated" && (
              <>
                <div className="p-4 rounded-lg bg-primary/5 border-l-4 border-primary">
                  <p>{layer.philosophical_meaning}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Dharmic Framing</p>
                  <p>{layer.dharmic_framing}</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/10">
                  <p className="text-sm text-muted-foreground">Sādhanā Application</p>
                  <p>{layer.sadhana_application}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reflection Questions */}
      {verse.reflection && (
        <Card className="bg-muted/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Reflection Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm">• {verse.reflection.question_1}</p>
            <p className="text-sm">• {verse.reflection.question_2}</p>
          </CardContent>
        </Card>
      )}

      {/* Source */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{verse.canonical?.scriptural_reference}</span>
        <div className="flex gap-2">
          {verse.canonical?.tags?.slice(0, 3).map((tag, i) => (
            <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
          ))}
        </div>
      </div>
    </div>
  );
};

// Story Display Component  
const StoryDisplay = ({ story, activeLayer, token }) => {
  const layer = story.layers?.[activeLayer];
  const storyTri = storyToTriItem({
    ...story,
    canonical: {
      ...story.canonical,
      short_story: activeLayer === "simple" ? story.canonical?.short_story : undefined,
      full_story: activeLayer !== "simple" ? story.canonical?.full_story : undefined,
      summary_en:
        activeLayer === "simple"
          ? story.canonical?.short_story || story.canonical?.summary_en
          : story.canonical?.full_story || story.canonical?.summary_en,
    },
  });

  return (
    <div className="space-y-6">
      <div className="prose max-w-none">
        <TriLanguageBlock
          item={storyTri}
          meaningPrefix="summary"
          meaningAudioMode="story"
          token={token}
        />
      </div>

      {story.canonical?.moral && (
        <div className="p-4 rounded-xl bg-gradient-to-r from-secondary/10 to-primary/10 border border-secondary/20">
          <TriLanguageBlock
            item={toTriFromFields({ english: story.canonical.moral })}
            meaningPrefix="translation"
            meaningAudioMode="meaning"
            token={token}
            englishLabel="The Moral"
          />
        </div>
      )}

      {/* Layer Content */}
      {layer && (
        <Card className="bg-white/80">
          <CardContent className="pt-6 space-y-4">
            {activeLayer === "simple" && (
              <>
                <TriLanguageBlock item={toTriFromFields({ english: layer.meaning })} meaningPrefix="translation" token={token} compact />
                <div className="p-3 rounded-lg bg-secondary/10">
                  <TriLanguageBlock item={toTriFromFields({ english: layer.application })} meaningPrefix="meaning" englishLabel="Today's Action" token={token} compact />
                </div>
              </>
            )}
            {activeLayer === "moderate" && (
              <>
                <p>{layer.explanation}</p>
                <p className="italic text-primary">{layer.emotional_resonance}</p>
                <div className="p-3 rounded-lg bg-secondary/10">
                  <p>{layer.daily_application}</p>
                </div>
              </>
            )}
            {activeLayer === "elevated" && (
              <>
                <div className="p-4 rounded-lg bg-primary/5 border-l-4 border-primary">
                  <p>{layer.philosophical_meaning}</p>
                </div>
                <p>{layer.dharmic_framing}</p>
                <div className="p-3 rounded-lg bg-secondary/10">
                  <p className="text-sm text-muted-foreground">Sādhanā Application</p>
                  <p>{layer.sadhana_application}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Source */}
      <p className="text-sm text-muted-foreground">
        Source: {story.canonical?.source_text}, {story.canonical?.chapter_reference}
      </p>
    </div>
  );
};

// Sacred Library Page
const ScripturesLibraryPage = ({ token }) => {
  const [verses, setVerses] = useState([]);
  const [stories, setStories] = useState([]);
  const [gurus, setGurus] = useState([]);
  const [events, setEvents] = useState([]);
  const [places, setPlaces] = useState([]);
  const [asanas, setAsanas] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [activeLayer, setActiveLayer] = useState("moderate");
  const [loading, setLoading] = useState(true);

  const openItem = (item, type) => {
    setSelectedItem(item);
    setSelectedType(type);
    const c = item.canonical || {};
    trackJourneyEvent(token, "library_view", item.id, c.title || c.name || item.id);
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [versesRes, storiesRes, gurusRes, eventsRes, placesRes, asanasRes] = await Promise.all([
          axios.get(`${API}/content/verses`),
          axios.get(`${API}/content/stories`),
          axios.get(`${API}/content/gurus`),
          axios.get(`${API}/content/events`),
          axios.get(`${API}/content/places`),
          axios.get(`${API}/content/yoga/asanas`).catch(() => ({ data: [] })),
        ]);
        setVerses(versesRes.data);
        setStories(storiesRes.data);
        setGurus(gurusRes.data);
        setEvents(eventsRes.data);
        setPlaces(placesRes.data);
        setAsanas(Array.isArray(asanasRes.data) ? asanasRes.data : asanasRes.data?.asanas || []);
      } catch (err) {
        console.error("Failed to fetch content:", err);
      }
      setLoading(false);
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const ContentModal = () => {
    if (!selectedItem || !selectedType) return null;

    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={() => { setSelectedItem(null); setSelectedType(null); }}
      >
        <Card 
          className="max-w-3xl w-full max-h-[90vh] overflow-auto bg-white"
          onClick={(e) => e.stopPropagation()}
          data-testid="library-content-modal"
        >
          <CardHeader className="border-b sticky top-0 bg-white z-10">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="font-heading text-2xl">
                  {selectedItem.canonical?.title || selectedItem.canonical?.name}
                </CardTitle>
                {selectedItem.canonical?.scriptural_reference && (
                  <p className="text-muted-foreground">{selectedItem.canonical.scriptural_reference}</p>
                )}
              </div>
              <button 
                onClick={() => { setSelectedItem(null); setSelectedType(null); }}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <LayerSelector activeLayer={activeLayer} setActiveLayer={setActiveLayer} />
          </CardHeader>
          <CardContent className="py-6">
            {selectedType === "verse" && <VerseDisplay verse={selectedItem} activeLayer={activeLayer} token={token} />}
            {selectedType === "story" && <StoryDisplay story={selectedItem} activeLayer={activeLayer} token={token} />}
            {selectedType === "asana" && (
              <TriLanguageBlock item={asanaToTriItem(selectedItem)} meaningPrefix="description" token={token} />
            )}
            {(selectedType === "guru" || selectedType === "event" || selectedType === "place") && (
              <div className="space-y-6">
                {selectedItem.canonical?.short_intro && (
                  <TriLanguageBlock
                    item={guruToTriItem(selectedItem)}
                    meaningPrefix="translation"
                    meaningAudioMode="commentary"
                    token={token}
                  />
                )}
                {selectedItem.canonical?.significance && (
                  <p>{selectedItem.canonical.significance}</p>
                )}
                {selectedType === "event" && selectedItem.canonical?.fasting_rules && (
                  <div><p className="text-sm font-medium">Fasting</p><p className="text-muted-foreground">{selectedItem.canonical.fasting_rules}</p></div>
                )}
                {selectedType === "event" && selectedItem.canonical?.timings && (
                  <div><p className="text-sm font-medium">Timings</p><p className="text-muted-foreground">{selectedItem.canonical.timings}</p></div>
                )}
                {selectedType === "event" && selectedItem.canonical?.mantra && (
                  <div>
                    <p className="text-sm font-medium mb-1">Mantra</p>
                    <MantraTriLanguageBlock
                      item={toTriFromFields({ sanskrit: selectedItem.canonical.mantra, english: selectedItem.canonical.mantra_meaning })}
                      meaningPrefix="translation"
                      token={token}
                    />
                  </div>
                )}
                {selectedType === "event" && selectedItem.canonical?.story && (
                  <TriLanguageBlock
                    item={storyToTriItem({
                      canonical: { summary_en: selectedItem.canonical.story, short_story: selectedItem.canonical.story },
                    })}
                    meaningPrefix="summary"
                    meaningAudioMode="story"
                    token={token}
                    className="text-muted-foreground italic"
                  />
                )}
                {selectedItem.canonical?.importance && (
                  <p>{selectedItem.canonical.importance}</p>
                )}
                {selectedItem.layers?.[activeLayer] && (
                  <Card className="bg-muted/30">
                    <CardContent className="pt-6 space-y-4">
                      {activeLayer === "simple" && (
                        <>
                          <p>{selectedItem.layers.simple.meaning}</p>
                          <p className="text-sm text-muted-foreground">{selectedItem.layers.simple.reflection}</p>
                        </>
                      )}
                      {activeLayer === "moderate" && (
                        <>
                          <p>{selectedItem.layers.moderate.explanation}</p>
                          <p className="italic">{selectedItem.layers.moderate.emotional_resonance}</p>
                        </>
                      )}
                      {activeLayer === "elevated" && (
                        <>
                          <p>{selectedItem.layers.elevated.philosophical_meaning}</p>
                          <p>{selectedItem.layers.elevated.dharmic_framing}</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </CardContent>
          {/* Share Button for verses */}
          {selectedType === "verse" && selectedItem?.id && (
            <div className="px-6 pb-6 pt-0 flex justify-end">
              <ShareQuoteButton 
                content={`${selectedItem.canonical?.english_meaning}\n\n— ${selectedItem.canonical?.scriptural_reference}`}
                type="verse"
                verseId={selectedItem.id}
              />
            </div>
          )}
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="scriptures-library-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Link to="/library" className="text-sm text-primary hover:underline inline-block mb-4">
            ← Sacred Library (deities, festivals, vratas)
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-4">Scriptures & Classic Library</h1>
          <p className="text-muted-foreground text-lg">Verses, Stories, Gurus, Events & Sacred Places</p>
          <p className="text-sm text-muted-foreground mt-2">Each piece of content is presented in three layers: Simple, Moderate, and Elevated</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="scriptures-puranas-row">
          {PURANA_TILES.map((purana) => (
            <Card
              key={purana.id}
              className="bg-gradient-to-br from-primary/5 via-white to-secondary/5 border-primary/20 hover:shadow-float transition-all"
            >
              <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="font-heading text-lg text-primary">{purana.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{purana.summary}</p>
                </div>
                <Button asChild size="sm" className="shrink-0">
                  <Link to={purana.path} data-testid={`scriptures-purana-link-${purana.id}`}>
                    Open Purāṇa
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="verses" className="w-full">
          <TabsList className="w-full flex flex-wrap justify-center mb-8 bg-transparent gap-2">
            <TabsTrigger value="verses" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Verses ({verses.length})
            </TabsTrigger>
            <TabsTrigger value="stories" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Stories ({stories.length})
            </TabsTrigger>
            <TabsTrigger value="gurus" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Gurus ({gurus.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Events ({events.length})
            </TabsTrigger>
            <TabsTrigger value="places" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Sacred Places ({places.length})
            </TabsTrigger>
            <TabsTrigger value="yoga" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Yoga ({asanas.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="verses">
            {verses.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No verses in the library yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {verses.map((verse) => (
                  <Card 
                    key={verse.id}
                    className="cursor-pointer hover:shadow-float transition-all bg-white/80"
                    onClick={() => openItem(verse, "verse")}
                    data-testid={`verse-card-${verse.id}`}
                  >
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-medium mb-2">{verse.canonical?.title}</h3>
                      <div className="mb-3 line-clamp-3">
                        <TriLanguageBlock item={verseCanonicalToTri(verse)} meaningPrefix="translation" compact token={token} />
                      </div>
                      <div className="mt-4 flex justify-between items-center">
                        <Badge variant="outline">{verse.canonical?.scriptural_reference}</Badge>
                        <span className="text-xs text-muted-foreground">Click to explore</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="stories">
            {stories.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No stories in the library yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {stories.map((story) => (
                  <StoryCard
                    key={story.id}
                    story={story}
                    token={token}
                    onClick={() => openItem(story, "story")}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="gurus">
            {gurus.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <User className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No guru profiles in the library yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {gurus.map((guru) => (
                  <GuruCard
                    key={guru.id}
                    guru={guru}
                    token={token}
                    onClick={() => openItem(guru, "guru")}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="events">
            {events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Sun className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No events in the library yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {events.map((event) => (
                  <Card 
                    key={event.id}
                    className="cursor-pointer hover:shadow-float transition-all bg-white/80"
                    onClick={() => openItem(event, "event")}
                    data-testid={`event-card-${event.id}`}
                  >
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-medium mb-2">{event.canonical?.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{event.canonical?.significance}</p>
                      <div className="flex gap-2">
                        <Badge>{event.canonical?.event_type}</Badge>
                        {event.canonical?.tithi && <Badge variant="outline">{event.canonical.tithi}</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="places">
            {places.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Compass className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No sacred places in the library yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {places.map((place) => (
                  <Card 
                    key={place.id}
                    className="cursor-pointer hover:shadow-float transition-all bg-white/80"
                    onClick={() => openItem(place, "place")}
                    data-testid={`place-card-${place.id}`}
                  >
                    <CardContent className="p-6">
                      <h3 className="font-heading text-lg font-medium mb-1">{place.canonical?.name}</h3>
                      {place.canonical?.sanskrit_name && (
                        <div className="mb-2">
                          <TriLanguageBlock
                            item={toTriFromFields({
                              sanskrit: place.canonical.sanskrit_name,
                              english: place.canonical.name,
                            })}
                            meaningPrefix="translation"
                            compact
                            token={token}
                          />
                        </div>
                      )}
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{place.canonical?.importance}</p>
                      <Badge variant="outline">{place.canonical?.location}</Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="yoga">
            {asanas.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Sun className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No yoga āsanas in the library yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {asanas.map((asana) => (
                  <YogaAsanaCard
                    key={asana.id}
                    asana={asana}
                    token={token}
                    onClick={() => openItem(asana, "asana")}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <ContentModal />
      </div>
    </div>
  );
};

// ==================== SHARE QUOTE COMPONENT ====================

const ShareQuoteButton = ({ content, type, verseId }) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareImage, setShareImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateShareImage = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        type === "verse" ? `${API}/share/verse` : `${API}/share/sadhana`,
        payload
      );
      setShareImage(response.data.image_base64);
    } catch (err) {
      console.error("Failed to generate share image:", err);
    }
    setLoading(false);
  };

  const shareOptions = [
    { name: "WhatsApp", icon: "💬", action: () => window.open(`https://wa.me/?text=${encodeURIComponent(content)}`) },
    { name: "Facebook", icon: "📘", action: () => window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(content)}`) },
    { name: "Copy", icon: "📋", action: () => { navigator.clipboard.writeText(content); alert("Copied to clipboard!"); } },
    { name: "Download Image", icon: "⬇️", action: async () => {
      if (!shareImage) await generateShareImage();
      if (shareImage) {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${shareImage}`;
        link.download = 'vedaPath-quote.png';
        link.click();
      }
    }}
  ];

  // Check if native share is available
  const hasNativeShare = typeof navigator.share === 'function';

  const handleNativeShare = async () => {
    if (hasNativeShare) {
      try {
        await navigator.share({
          title: 'VedaPath',
          text: content,
          url: window.location.href
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    }
  };

  return (
    <div className="relative">
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => hasNativeShare ? handleNativeShare() : setShowShareMenu(!showShareMenu)}
        className="gap-2"
        data-testid="share-quote-btn"
      >
        <Share2 className="w-4 h-4" /> Share
      </Button>
      
      {showShareMenu && !hasNativeShare && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-float border border-border p-3 z-50 min-w-[180px]">
          {shareOptions.map((option, i) => (
            <button
              key={i}
              onClick={() => { option.action(); setShowShareMenu(false); }}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted flex items-center gap-3 text-sm"
              disabled={loading && option.name === "Download Image"}
            >
              <span>{option.icon}</span>
              <span>{option.name}</span>
              {loading && option.name === "Download Image" && <Loader2 className="w-3 h-3 animate-spin" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== PANCHANGA (VEDIC CALENDAR) PAGE ====================

const PANCHANGA_LOC_KEY = "vedapath_panchanga_loc";

const TITHI_BADGE_VARIANT = {
  purnima: "secondary",
  amavasya: "outline",
  ekadashi: "default",
  ashtami: "outline",
  navami: "outline",
  trayodashi: "outline",
  chaturdashi: "outline",
};

function PanchangaDayDetail({ day, onClose }) {
  if (!day) return null;
  const puja = day.puja_vidhi;
  const sadhana = day.sadhana_guide;
  const doDont = day.do_and_dont;

  return (
    <Card className="bg-white/95 border-primary/20 shadow-float" data-testid="panchanga-day-detail">
      <CardHeader>
        <div className="flex justify-between items-start gap-4">
          <div>
            <CardTitle className="font-heading text-xl">
              {day.gregorian?.weekday}, {day.gregorian?.month} {day.gregorian?.day}, {day.gregorian?.year}
            </CardTitle>
            <CardDescription>
              {day.tithi?.full_name} · {day.nakshatra?.name}
              {day.cultural_layer?.telugu_masa && ` · ${day.cultural_layer.telugu_masa}`}
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 text-sm">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="font-medium mb-1">Tithi (at sunrise)</p>
            <p>{day.tithi?.full_name}</p>
            {day.tithi?.ends_at_local_hm && (
              <p className="text-muted-foreground mt-1">Ends {day.tithi.ends_at_local_hm} local</p>
            )}
          </div>
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="font-medium mb-1">Nakṣatra</p>
            <p>{day.nakshatra?.name}</p>
            {day.nakshatra?.ends_at_local_hm && (
              <p className="text-muted-foreground mt-1">Ends {day.nakshatra.ends_at_local_hm} local</p>
            )}
          </div>
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="font-medium mb-1 flex items-center gap-1">
              <Sun className="w-4 h-4" /> Sun
            </p>
            <p>Rise {day.sunrise ?? "—"}</p>
            <p>Set {day.sunset ?? "—"}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="font-medium mb-1 flex items-center gap-1">
              <Moon className="w-4 h-4" /> Moon
            </p>
            <p>Rise {day.moonrise_local ?? "—"}</p>
            <p>Set {day.moonset_local ?? "—"}</p>
            <p className="text-muted-foreground mt-1">{day.moon_illumination}% illuminated</p>
          </div>
        </div>

        {day.festivals?.length > 0 && (
          <div>
            <p className="font-medium mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Festivals
            </p>
            <ul className="space-y-2">
              {day.festivals.map((f) => {
                const libTo =
                  libraryLinkForFestival(f.id) || libraryLinkForFestivalName(f.name_en);
                const imgUrl = libraryImageForFestival(f.id);
                return (
                  <li key={f.id} className="p-3 rounded-lg border border-border/50 flex items-start gap-3">
                    {imgUrl && (
                      <FestivalImage
                        src={imgUrl}
                        alt={f.name_en}
                        size="thumb"
                        className="w-10 h-10 flex-shrink-0 mt-1"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{f.name_en}</p>
                      {f.name_te && <p className="text-primary text-xs">{f.name_te}</p>}
                      {f.notes && <p className="text-muted-foreground mt-1">{f.notes}</p>}
                      {libTo && (
                        <div className="mt-2">
                          <LibraryTopicLink to={libTo}>Sacred Library →</LibraryTopicLink>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {day.vratas?.length > 0 && (
          <div>
            <p className="font-medium mb-2">Vratas</p>
            <ul className="space-y-1">
              {day.vratas.map((v) => {
                const libTo =
                  libraryLinkForVrata(v.id) || libraryLinkForVrataName(v.name_en);
                const imgUrl = libraryImageForVrata(v.id);
                return (
                  <li key={v.id} className="flex items-start gap-3 py-2 px-1">
                    {imgUrl && (
                      <SacredImage
                        src={imgUrl}
                        alt={v.name_en}
                        variant="vrata"
                        size="thumb"
                        className="w-10 h-10 flex-shrink-0 mt-1"
                      />
                    )}
                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{v.name_en}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {v.category}
                        </Badge>
                      </div>
                      {libTo && <LibraryTopicLink to={libTo}>Sacred Library →</LibraryTopicLink>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {day.story && (
          <div>
            <p className="font-medium mb-1">Story</p>
            <p className="text-muted-foreground italic">{day.story}</p>
          </div>
        )}

        {puja && (
          <div>
            <p className="font-medium mb-1">Puja vidhi</p>
            {puja.general?.length > 0 && (
              <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                {puja.general.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
            {puja.festival_specific?.map((line, i) => (
              <p key={i} className="text-muted-foreground mt-1">
                {line}
              </p>
            ))}
          </div>
        )}

        {sadhana && (
          <div>
            <p className="font-medium mb-1">Sādhana guide</p>
            <ul className="space-y-1 text-muted-foreground">
              {Object.entries(sadhana).map(([k, v]) => (
                <li key={k}>
                  <span className="capitalize text-foreground">{k.replace(/_/g, " ")}: </span>
                  {v}
                </li>
              ))}
            </ul>
          </div>
        )}

        {day.mantra && (
          <div>
            <p className="font-medium mb-1">Mantra</p>
            <p className="text-primary">{day.mantra}</p>
          </div>
        )}

        {day.vedic_history_note && (
          <div>
            <p className="font-medium mb-1">Vedic history</p>
            <p className="text-muted-foreground">{day.vedic_history_note}</p>
          </div>
        )}

        {doDont && (
          <div className="grid sm:grid-cols-2 gap-4">
            {doDont.do?.length > 0 && (
              <div>
                <p className="font-medium mb-1">Do</p>
                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                  {doDont.do.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            )}
            {doDont.dont?.length > 0 && (
              <div>
                <p className="font-medium mb-1">Avoid</p>
                <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                  {doDont.dont.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {day.fasting_rules && (
          <div>
            <p className="font-medium mb-1">Fasting</p>
            <ul className="text-muted-foreground space-y-1">
              {Object.entries(day.fasting_rules).map(([k, v]) => (
                <li key={k}>
                  <span className="capitalize">{k.replace(/_/g, " ")}: </span>
                  {v}
                </li>
              ))}
            </ul>
          </div>
        )}

        {day.charity_suggestion && (
          <div>
            <p className="font-medium mb-1">Charity</p>
            <p className="text-muted-foreground">{day.charity_suggestion}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UtsavaSpanEnrichment({ sourceDay }) {
  const d = sourceDay;
  if (!d) return <p className="text-muted-foreground text-sm">No detail for this span.</p>;
  return (
    <div className="space-y-3 text-sm text-muted-foreground">
      {d.story && (
        <p>
          <span className="font-medium text-foreground">Story: </span>
          {d.story}
        </p>
      )}
      {d.puja_vidhi?.general?.length > 0 && (
        <div>
          <p className="font-medium text-foreground">Puja vidhi</p>
          <ul className="list-disc pl-5 mt-1">
            {d.puja_vidhi.general.slice(0, 5).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {d.sadhana_guide && (
        <div>
          <p className="font-medium text-foreground">Sādhana</p>
          {Object.entries(d.sadhana_guide).map(([k, v]) => (
            <p key={k} className="mt-1">
              <span className="capitalize">{k.replace(/_/g, " ")}: </span>
              {v}
            </p>
          ))}
        </div>
      )}
      {d.mantra && (
        <p>
          <span className="font-medium text-foreground">Mantra: </span>
          <span className="text-primary">{d.mantra}</span>
        </p>
      )}
      {d.do_and_dont && (
        <div className="grid sm:grid-cols-2 gap-2">
          {d.do_and_dont.do?.length > 0 && (
            <div>
              <p className="font-medium text-foreground">Do</p>
              <ul className="list-disc pl-5">
                {d.do_and_dont.do.slice(0, 4).map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          )}
          {d.do_and_dont.dont?.length > 0 && (
            <div>
              <p className="font-medium text-foreground">Avoid</p>
              <ul className="list-disc pl-5">
                {d.do_and_dont.dont.slice(0, 4).map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {d.vedic_history_note && (
        <p>
          <span className="font-medium text-foreground">History: </span>
          {d.vedic_history_note}
        </p>
      )}
    </div>
  );
}

const PanchangaPage = ({ token }) => {
  const [todayPanchanga, setTodayPanchanga] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState("en");
  const [locSaving, setLocSaving] = useState(false);
  const [locMessage, setLocMessage] = useState("");
  const [loc, setLoc] = useState(() => {
    try {
      const raw = localStorage.getItem(PANCHANGA_LOC_KEY);
      if (!raw) return { lat: 17.385, lon: 78.4867, tz_offset_minutes: 330 };
      const s = JSON.parse(raw);
      if (typeof s.lat === "number" && typeof s.lon === "number" && typeof s.tz_offset_minutes === "number") {
        return s;
      }
    } catch (_) {}
    return { lat: 17.385, lon: 78.4867, tz_offset_minutes: 330 };
  });

  useEffect(() => {
    try {
      localStorage.setItem(PANCHANGA_LOC_KEY, JSON.stringify(loc));
    } catch (_) {}
  }, [loc]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/panchanga/location`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const saved = res.data?.saved;
        if (saved && typeof saved.lat === "number") {
          setLoc({
            lat: saved.lat,
            lon: saved.lon,
            tz_offset_minutes: saved.tz_offset_minutes,
            city: saved.city,
            country: saved.country,
          });
        }
      } catch (_) {}
    })();
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const qLoc = `&lat=${encodeURIComponent(loc.lat)}&lon=${encodeURIComponent(loc.lon)}&tz_offset_minutes=${encodeURIComponent(loc.tz_offset_minutes)}`;
        const langQ = `?lang=${encodeURIComponent(language)}${qLoc}`;
        const [todayRes, eventsRes] = await Promise.all([
          axios.get(`${API}/panchanga/today${langQ}`),
          axios.get(`${API}/calendar/events`),
        ]);
        setTodayPanchanga(todayRes.data);
        setEvents(eventsRes.data);
        const monthQs = `year=${currentYear}&month=${currentMonth}&lang=${encodeURIComponent(language)}${qLoc}`;
        const monthRes = await axios.get(`${API}/panchanga/month?${monthQs}`);
        setMonthlyData(monthRes.data);
      } catch (err) {
        console.error("Failed to fetch panchanga:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [currentYear, currentMonth, language, loc.lat, loc.lon, loc.tz_offset_minutes]);

  const changeMonth = async (delta) => {
    let newMonth = currentMonth + delta;
    let newYear = currentYear;

    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }

    setCurrentMonth(newMonth);
    setCurrentYear(newYear);

    try {
      const qLoc = `&lat=${encodeURIComponent(loc.lat)}&lon=${encodeURIComponent(loc.lon)}&tz_offset_minutes=${encodeURIComponent(loc.tz_offset_minutes)}`;
      const monthQs = `year=${newYear}&month=${newMonth}&lang=${encodeURIComponent(language)}${qLoc}`;
      const monthRes = await axios.get(`${API}/panchanga/month?${monthQs}`);
      setMonthlyData(monthRes.data);
    } catch (err) {
      console.error("Failed to fetch month:", err);
    }
  };

  const detectBrowserLocation = () => {
    setLocMessage("");
    if (!navigator.geolocation) {
      setLocMessage("Geolocation not supported in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const tz = -new Date().getTimezoneOffset();
        setLoc({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          tz_offset_minutes: tz,
          source: "browser",
        });
        setLocMessage("Location updated from browser. Verify timezone offset.");
      },
      () => setLocMessage("Could not read geolocation. Enter coordinates manually."),
      { maximumAge: 600000, timeout: 15000 }
    );
  };

  const saveProfileLocation = async () => {
    setLocMessage("");
    if (!token) {
      setLocMessage("Sign in to save location to your profile (stored locally until then).");
      return;
    }
    setLocSaving(true);
    try {
      await axios.post(
        `${API}/panchanga/set-location`,
        {
          lat: loc.lat,
          lon: loc.lon,
          tz_offset_minutes: loc.tz_offset_minutes,
          source: "manual",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocMessage("Saved to your profile.");
    } catch (e) {
      setLocMessage("Could not save profile location.");
    }
    setLocSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const labels = todayPanchanga?.multilingual_labels?.[language] || todayPanchanga?.multilingual_labels?.en;

  const monthNames = ["", "January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];

  const monthDays = monthlyData?.days ?? [];
  const gridBadges = computeDayGridBadges(monthDays);
  const utsavaSpans = buildUtsavaSpans(monthDays, monthNames[currentMonth]);
  const paddedMonthDays = padMonthDays(monthDays, currentYear, currentMonth);

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="panchanga-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-2">Pañchāṅga</h1>
          <p className="text-muted-foreground text-lg">पञ्चाङ्ग — The Vedic Calendar</p>
        </div>

        {/* Language Selector */}
        <div className="flex justify-center mb-8">
          <select 
            value={language} 
            onChange={(e) => setLanguage(e.target.value)}
            className="px-4 py-2 rounded-lg border border-border bg-white/80"
            data-testid="language-selector"
          >
            <option value="en">English</option>
            <option value="hi">हिन्दी</option>
            <option value="sa">संस्कृतम्</option>
            <option value="ta">தமிழ்</option>
            <option value="te">తెలుగు</option>
            <option value="kn">ಕನ್ನಡ</option>
            <option value="ml">മലയാളം</option>
            <option value="mr">मराठी</option>
            <option value="gu">ગુજરાતી</option>
            <option value="bn">বাংলা</option>
          </select>
        </div>

        <Card className="mb-8 bg-white/85 border-border/60" data-testid="panchanga-location">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Sun className="w-5 h-5 text-primary" />
              Location for Drik gaṇita
            </CardTitle>
            <CardDescription>
              Sunrise-bound tithi and local rise/set times use latitude, longitude, and fixed UTC offset (minutes east, e.g. 330 for IST).
              {todayPanchanga?.location_resolution && (
                <span className="block mt-1 text-xs">
                  Source: {todayPanchanga.location_resolution.source}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="p-lat">Latitude</Label>
              <Input
                id="p-lat"
                type="number"
                step="any"
                value={loc.lat}
                onChange={(e) => setLoc({ ...loc, lat: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="p-lon">Longitude</Label>
              <Input
                id="p-lon"
                type="number"
                step="any"
                value={loc.lon}
                onChange={(e) => setLoc({ ...loc, lon: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="p-tz">TZ offset (min E)</Label>
              <Input
                id="p-tz"
                type="number"
                step="1"
                value={loc.tz_offset_minutes}
                onChange={(e) => setLoc({ ...loc, tz_offset_minutes: Number(e.target.value) })}
              />
            </div>
            <div className="flex flex-col gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={detectBrowserLocation}>
                Auto (browser)
              </Button>
              <Button type="button" variant="outline" onClick={saveProfileLocation} disabled={locSaving}>
                {locSaving ? "Saving…" : "Save to profile"}
              </Button>
            </div>
          </CardContent>
          {locMessage && <p className="text-sm text-muted-foreground px-6 pb-4">{locMessage}</p>}
        </Card>

        {/* Today's Panchanga */}
        {todayPanchanga && (
          <Card className="mb-8 bg-gradient-to-br from-primary/5 to-secondary/10 shadow-float" data-testid="today-panchanga">
            <CardHeader>
              <CardTitle className="font-heading text-2xl flex items-center gap-3">
                <Moon className="w-6 h-6 text-secondary" />
                {labels?.today || "Today's Vedic Day"}
              </CardTitle>
              <CardDescription>
                {todayPanchanga.gregorian?.weekday}, {todayPanchanga.gregorian?.month} {todayPanchanga.gregorian?.day}, {todayPanchanga.gregorian?.year}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Tithi */}
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground">{labels?.tithi || "Tithi"}</p>
                  <TriLanguageIfSanskrit
                    item={toTriFromFields({
                      sanskrit: todayPanchanga.tithi?.sanskrit,
                      english: todayPanchanga.tithi?.full_name || todayPanchanga.tithi?.name,
                    })}
                    meaningPrefix="translation"
                    compact
                  />
                  <Badge variant="outline" className="mt-2">{todayPanchanga.tithi?.nature}</Badge>
                </div>

                {/* Nakshatra */}
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground">{labels?.nakshatra || "Nakshatra"}</p>
                  <TriLanguageIfSanskrit
                    item={toTriFromFields({
                      sanskrit: todayPanchanga.nakshatra?.sanskrit,
                      english: todayPanchanga.nakshatra?.name,
                    })}
                    meaningPrefix="translation"
                    compact
                  />
                  <p className="text-xs text-muted-foreground mt-2">Deity: {todayPanchanga.nakshatra?.deity}</p>
                </div>

                {/* Paksha */}
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground">{labels?.paksha || "Paksha"}</p>
                  <TriLanguageIfSanskrit
                    item={toTriFromFields({
                      sanskrit: todayPanchanga.paksha?.sanskrit,
                      english: todayPanchanga.paksha?.name_localized || todayPanchanga.paksha?.name,
                    })}
                    meaningPrefix="translation"
                    compact
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <Moon className="w-4 h-4" />
                    <span className="text-sm">{todayPanchanga.moon_illumination}%</span>
                  </div>
                </div>

                {/* Vedic Month */}
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground">{labels?.month || "Month"}</p>
                  <TriLanguageIfSanskrit
                    item={toTriFromFields({
                      sanskrit: todayPanchanga.vedic_month?.sanskrit,
                      english: todayPanchanga.vedic_month?.name,
                    })}
                    meaningPrefix="translation"
                    compact
                  />
                  <p className="text-xs text-muted-foreground mt-2">{todayPanchanga.vedic_month?.season}</p>
                  {todayPanchanga.vedic_month?.telugu && (
                    <p className="text-xs text-primary mt-1">{todayPanchanga.vedic_month.telugu}</p>
                  )}
                  {todayPanchanga.cultural_layer?.is_adhika_masa && (
                    <Badge variant="secondary" className="mt-2 text-[10px]">
                      అధిక మాసము — Adhika māsa
                    </Badge>
                  )}
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Sun className="w-4 h-4" /> Sun
                  </p>
                  <p className="font-medium">Rise {todayPanchanga.sunrise}</p>
                  <p className="font-medium">Set {todayPanchanga.sunset}</p>
                </div>
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Moon className="w-4 h-4" /> Moon
                  </p>
                  <p className="font-medium">Rise {todayPanchanga.moonrise_local ?? "—"}</p>
                  <p className="font-medium">Set {todayPanchanga.moonset_local ?? "—"}</p>
                </div>
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground">Limb endings (local)</p>
                  <p className="text-sm">Tithi → {todayPanchanga.tithi?.ends_at_local_hm}</p>
                  <p className="text-sm">Nakṣatra → {todayPanchanga.nakshatra?.ends_at_local_hm}</p>
                </div>
                <div className="p-4 rounded-xl bg-white/60 border border-border/40">
                  <p className="text-sm text-muted-foreground">Engine</p>
                  <p className="text-xs font-mono">{todayPanchanga.engine || "legacy"}</p>
                  {todayPanchanga.cultural_layer?.telugu_masa && (
                    <p className="text-sm mt-1">Telugu māsa: {todayPanchanga.cultural_layer.telugu_masa}</p>
                  )}
                </div>
              </div>

              {(todayPanchanga.festivals?.length > 0 || todayPanchanga.vratas?.length > 0) && (
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  {todayPanchanga.festivals?.length > 0 && (
                    <div className="p-4 rounded-xl bg-secondary/5 border border-secondary/20">
                      <p className="font-medium mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Festivals today
                      </p>
                      <ul className="text-sm space-y-1">
                        {todayPanchanga.festivals.map((f) => (
                          <li key={f.id}>
                            <span className="font-medium">{f.name_en}</span>
                            {f.notes && <span className="text-muted-foreground"> — {f.notes}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {todayPanchanga.vratas?.length > 0 && (
                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                      <p className="font-medium mb-2">Vratas today</p>
                      <ul className="text-sm space-y-1">
                        {todayPanchanga.vratas.map((v) => (
                          <li key={v.id}>
                            {v.name_en} <Badge variant="outline" className="ml-1 text-[10px]">{v.category}</Badge>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {(todayPanchanga.puja_vidhi || todayPanchanga.sadhana_guide || todayPanchanga.mantra) && (
                <div className="mt-6 p-4 rounded-xl bg-muted/20 border border-border/40 space-y-4">
                  <h4 className="font-medium">Puja &amp; sādhana</h4>
                  {todayPanchanga.mantra && (
                    <p className="text-sm">
                      <span className="font-medium">Mantra: </span>
                      <span className="text-primary">{todayPanchanga.mantra}</span>
                    </p>
                  )}
                  {todayPanchanga.sadhana_guide && (
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      {Object.entries(todayPanchanga.sadhana_guide).map(([k, v]) => (
                        <li key={k}>
                          <span className="capitalize text-foreground font-medium">{k.replace(/_/g, " ")}: </span>
                          {v}
                        </li>
                      ))}
                    </ul>
                  )}
                  {todayPanchanga.astrological_events?.filter(
                    (x) => !/three p[uū]rṇim|purnima_day_count/i.test(String(x))
                  ).length > 0 && (
                    <div>
                      <p className="font-medium text-sm mb-1">Astrological notes</p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        {todayPanchanga.astrological_events
                          .filter((x) => !/three p[uū]rṇim|purnima_day_count/i.test(String(x)))
                          .map((x, i) => (
                            <li key={i}>{x}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Special Day */}
              {todayPanchanga.special_day && (
                <div className="mt-6 p-4 rounded-xl bg-secondary/10 border border-secondary/30">
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-secondary" />
                    <span className="font-medium text-lg">{todayPanchanga.special_day}</span>
                  </div>
                </div>
              )}

              {/* Recommended Practices */}
              {todayPanchanga.recommended_practices && (
                <div className="mt-6">
                  <h4 className="font-medium mb-3">Recommended Sādhanā</h4>
                  <ul className="space-y-2">
                    {todayPanchanga.recommended_practices.map((practice, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-secondary">•</span>
                        <span>{practice}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Monthly Calendar */}
        <Card className="mb-8 bg-white/90" data-testid="monthly-calendar">
          <CardHeader>
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => changeMonth(-1)}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <CardTitle className="font-heading text-xl">
                {monthNames[currentMonth]} {currentYear}
              </CardTitle>
              <Button variant="outline" onClick={() => changeMonth(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {monthlyData?.adhika_masa_present_in_month && (
              <p className="text-sm text-primary mb-3 px-1">
                This civil month includes <strong>adhika māsa</strong> (extra lunar month). Tithi badges use sunrise at your location.
              </p>
            )}
            <div className="grid grid-cols-7 gap-1 text-center mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="p-2 font-medium text-sm text-muted-foreground">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {paddedMonthDays.map((day, i) => {
                const padCount = paddedMonthDays.length - monthDays.length;
                const dayIndex = i - padCount;
                const badges =
                  day && dayIndex >= 0 && dayIndex < gridBadges.length ? gridBadges[dayIndex] : null;
                const isSelected = day?.date && selectedDate?.date === day.date;
                return (
                  <div
                    key={day?.date ?? `pad-${i}`}
                    className={`p-2 rounded-lg min-h-[56px] cursor-pointer transition-colors flex flex-col items-center gap-1 ${
                      day
                        ? isSelected
                          ? "bg-primary/15 border-2 border-primary ring-1 ring-primary/30"
                          : "bg-white/70 border border-border/40 hover:bg-white hover:border-border"
                        : "bg-transparent border-transparent"
                    }`}
                    onClick={() => day && setSelectedDate(day)}
                    data-testid={day ? `calendar-day-${day.gregorian?.day}` : undefined}
                  >
                    {day && (
                      <>
                        <p className="font-medium text-sm w-full text-center">{day.gregorian?.day}</p>
                        <div className="flex flex-wrap gap-0.5 justify-center w-full">
                          {badges?.tithiBadges?.map((b) => (
                            <Badge
                              key={b.key}
                              variant={TITHI_BADGE_VARIANT[b.key] || "outline"}
                              className="text-[9px] px-1 py-0 leading-tight"
                            >
                              {b.label}
                            </Badge>
                          ))}
                          {badges?.festivals?.map((f) => (
                            <Badge
                              key={f.id}
                              variant="secondary"
                              className="text-[9px] px-1 py-0 leading-tight max-w-full truncate"
                              title={f.name_en}
                            >
                              {f.name_en.length > 14 ? `${f.name_en.slice(0, 12)}…` : f.name_en}
                            </Badge>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {utsavaSpans.length > 0 && (
          <Card className="mb-8 bg-white/90" data-testid="utsava-spanning-month">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Utsava Spanning This Month</CardTitle>
              <CardDescription>Multi-day observances in {monthNames[currentMonth]} {currentYear}</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {utsavaSpans.map((span) => (
                  <AccordionItem key={`${span.id}-${span.startIndex}`} value={span.id + String(span.startIndex)}>
                    <AccordionTrigger className="text-left font-medium hover:no-underline">
                      {span.name}
                      <span className="text-muted-foreground font-normal ml-2">· {span.dateRange}</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <UtsavaSpanEnrichment sourceDay={span.sourceDay} />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        )}

        {selectedDate && (
          <div className="mb-8">
            <PanchangaDayDetail day={selectedDate} onClose={() => setSelectedDate(null)} />
          </div>
        )}

        {/* Upcoming Events */}
        <Card className="bg-white/90">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Upcoming Sacred Events</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="ekadashi">
              <TabsList className="mb-4">
                <TabsTrigger value="ekadashi">Ekādaśī</TabsTrigger>
                <TabsTrigger value="purnima">Pūrṇimā</TabsTrigger>
                <TabsTrigger value="jayanti">Jayantī</TabsTrigger>
                <TabsTrigger value="utsava">Utsava</TabsTrigger>
              </TabsList>
              
              {["ekadashi", "purnima", "jayanti", "utsava"].map((type) => (
                <TabsContent key={type} value={type}>
                  <div className="grid md:grid-cols-2 gap-4">
                    {events.filter(e => e.type === type).slice(0, 6).map((event, i) => (
                      <div
                        key={event.id || i}
                        className="p-4 rounded-lg bg-muted/30 border border-border/50 cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedEvent(event)}
                        role="button"
                        tabIndex={0}
                      >
                        <p className="font-medium">{event.title || event.name}</p>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {event.simple || event.significance}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {event.month} • {event.paksha} {event.tithi > 0 ? `• Tithi ${event.tithi}` : ""}
                        </p>
                        {event.deity && (
                          <Badge variant="outline" className="mt-2">{event.deity}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {selectedEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedEvent(null)}>
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto bg-white" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="font-heading text-2xl">{selectedEvent.title || selectedEvent.name}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedEvent(null)}><X className="w-4 h-4" /></Button>
                </div>
                {selectedEvent.significance && <CardDescription>{selectedEvent.significance}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {selectedEvent.simple && <p>{selectedEvent.simple}</p>}
                {selectedEvent.fasting_rules && (
                  <div><p className="font-medium">Fasting</p><p className="text-muted-foreground">{selectedEvent.fasting_rules}</p></div>
                )}
                {selectedEvent.timings && (
                  <div><p className="font-medium">Timings</p><p className="text-muted-foreground">{selectedEvent.timings}</p></div>
                )}
                {selectedEvent.mantra && (
                  <div>
                    <p className="font-medium mb-1">Mantra</p>
                    <MantraTriLanguageBlock item={mantraToTriItem({ text: selectedEvent.mantra, meaning: selectedEvent.mantra_meaning })} />
                  </div>
                )}
                {selectedEvent.story && (
                  <div><p className="font-medium">Story</p><p className="text-muted-foreground italic">{selectedEvent.story}</p></div>
                )}
                {selectedEvent.dos?.length > 0 && (
                  <div>
                    <p className="font-medium mb-1">Do</p>
                    <ul className="list-disc pl-5 text-muted-foreground">{selectedEvent.dos.map((d, i) => <li key={i}>{d}</li>)}</ul>
                  </div>
                )}
                {selectedEvent.donts?.length > 0 && (
                  <div>
                    <p className="font-medium mb-1">Avoid</p>
                    <ul className="list-disc pl-5 text-muted-foreground">{selectedEvent.donts.map((d, i) => <li key={i}>{d}</li>)}</ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== PRAYER HALL (DHYĀNA MANDIRA) PAGE ====================

const PrayerHallPageInner = ({ token }) => {
  const L = useCmsLabels();
  const [modes, setModes] = useState(null);
  const [seekerStates, setSeekerStates] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [userSeekerState, setUserSeekerState] = useState("curious");
  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(15);
  const [loading, setLoading] = useState(true);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioLoop, setAudioLoop] = useState(true);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [customMantras, setCustomMantras] = useState([]);
  const [selectedCustomMantra, setSelectedCustomMantra] = useState(null);
  const [mandirChantId, setMandirChantId] = useState(null);
  const timerRef = React.useRef(null);
  const audioRef = React.useRef(null);

  useEffect(() => {
    const fetchModes = async () => {
      try {
        const response = await axios.get(`${API}/prayer-hall/modes`);
        setModes(response.data.modes);
        setSeekerStates(response.data.seeker_states);
      } catch (err) {
        console.error("Failed to fetch modes:", err);
      }
      setLoading(false);
    };

    fetchModes();
  }, []);

  useEffect(() => {
    if (!token) {
      setCustomMantras([]);
      return;
    }
    axios
      .get(`${API}/user/mantras`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCustomMantras(r.data.mantras || []))
      .catch(() => {});
  }, [token]);

  const completeSession = useCallback(async () => {
    if (!activeSession) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/prayer-hall/complete/${activeSession.session_id}`,
        {},
        { headers }
      );
    } catch (err) {
      console.error("Failed to complete session:", err);
    }
  }, [activeSession, token]);

  useEffect(() => {
    if (isPlaying && timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setIsPlaying(false);
            clearInterval(timerRef.current);
            completeSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isPlaying, timer, completeSession]);

  const fetchModes = async () => {
    try {
      const response = await axios.get(`${API}/prayer-hall/modes`);
      setModes(response.data.modes);
      setSeekerStates(response.data.seeker_states);
    } catch (err) {
      console.error("Failed to fetch modes:", err);
    }
    setLoading(false);
  };

  const startSession = async (modeId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        mode: modeId,
        duration_minutes: duration,
        seeker_state: userSeekerState,
      };
      if (selectedCustomMantra) {
        payload.custom_mantra = {
          id: selectedCustomMantra.id,
          text: selectedCustomMantra.text,
          meaning: selectedCustomMantra.meaning,
        };
      }
      const response = await axios.post(`${API}/prayer-hall/start`, payload, { headers });
      const data = response.data;
      if (selectedCustomMantra) {
        data.custom_mantra = selectedCustomMantra;
      }
      setActiveSession(data);
      setSelectedMode(modeId);
      setTimer(duration * 60);
      setIsPlaying(true);
    } catch (err) {
      console.error("Failed to start session:", err);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const modeIcons = {
    guided_breath: Wind,
    guided_japa: Volume2,
    timer: Timer,
    ambient: Cloud,
    silence: Moon,
    adaptive: Sparkles,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Active Session View
  if (activeSession) {
    const ModeIcon = modeIcons[activeSession.mode] || Moon;
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 py-24 px-4" data-testid="prayer-hall-page">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <ModeIcon className="w-12 h-12 text-primary" />
            </div>
            <h1 className="font-heading text-3xl font-medium mb-2">
              {activeSession.guidance.mode_name}
            </h1>
            {activeSession.guidance.mode_sanskrit && (
              <TriLanguageBlock
                item={{ sanskrit_devanagari: activeSession.guidance.mode_sanskrit }}
                meaningPrefix="translation"
                compact
                className="text-center"
                token={token}
              />
            )}
          </div>

          {/* Timer Display */}
          <div className="mb-8">
            <div className="text-6xl font-mono font-light text-foreground mb-4">
              {formatTime(timer)}
            </div>
            <Progress value={((duration * 60 - timer) / (duration * 60)) * 100} className="h-2" />
          </div>

          {(activeSession.audio?.url || activeSession.guidance?.audio?.url) && (
            <Card className="bg-white/80 backdrop-blur-sm mb-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <Volume2 className="w-5 h-5 text-primary" />
                    <span className="font-medium">
                      {activeSession.audio?.title || "Sacred Sound"}
                    </span>
                  </div>
                  {!audioUnlocked ? (
                    <Button variant="default" size="sm" onClick={() => { setAudioUnlocked(true); audioRef.current?.play().catch(() => {}); setAudioPlaying(true); }}>
                      Enable sound
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const el = audioRef.current;
                        if (!el) return;
                        if (audioPlaying) { el.pause(); setAudioPlaying(false); }
                        else { el.play().catch(() => {}); setAudioPlaying(true); }
                      }}
                    >
                      {audioPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      {audioPlaying ? "Pause" : "Play"}
                    </Button>
                  )}
                </div>
                <audio
                  ref={audioRef}
                  loop={audioLoop}
                  src={prayerAudioSrc(activeSession)}
                  onTimeUpdate={() => {
                    const el = audioRef.current;
                    if (el?.duration) setAudioProgress((el.currentTime / el.duration) * 100);
                  }}
                  onEnded={() => {
                    if (!audioLoop) setAudioPlaying(false);
                  }}
                  className="hidden"
                />
                <div className="flex items-center justify-between mt-2">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={audioLoop}
                      onChange={(e) => {
                        setAudioLoop(e.target.checked);
                        if (audioRef.current) audioRef.current.loop = e.target.checked;
                      }}
                    />
                    Loop audio
                  </label>
                </div>
                <Progress value={audioProgress} className="h-1.5" />
              </CardContent>
            </Card>
          )}

          {activeSession.custom_mantra && (
            <Card className="bg-white/80 backdrop-blur-sm mb-6" data-testid="active-custom-mantra">
              <CardContent className="p-6 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Your Mantra</p>
                <MantraTriLanguageBlock item={mantraToTriItem(activeSession.custom_mantra)} token={token} />
              </CardContent>
            </Card>
          )}

          {/* Opening Blessing */}
          <Card className="bg-white/80 backdrop-blur-sm mb-6">
            <CardContent className="p-6">
              <TriLanguageBlock
                item={toTriFromFields({ english: activeSession.guidance.opening_blessing, meaning_en: activeSession.guidance.opening_blessing })}
                meaningPrefix="meaning"
                meaningAudioMode="sadhana"
                token={token}
                className="text-muted-foreground italic text-lg text-center"
              />
            </CardContent>
          </Card>

          {/* Mode-specific Instructions */}
          {activeSession.guidance.instructions && (
            <Card className="bg-white/80 backdrop-blur-sm mb-6">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{L.practiceInstructions}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-left">
                  {activeSession.guidance.instructions.map((inst, i) => (
                    <SadhanaStep key={i} text={inst} index={i} token={token} />
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Breathing Patterns for Pranayama */}
          {activeSession.mode === "guided_breath" && activeSession.guidance.breathing_patterns && (
            <Card className="bg-white/80 backdrop-blur-sm mb-6">
              <CardHeader>
                <CardTitle className="font-heading text-lg">{L.breathingPatterns}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {activeSession.guidance.breathing_patterns.map((pattern, i) => (
                    <div key={i} className="p-3 rounded-lg bg-muted/30 text-left">
                      <p className="font-medium mb-1">{pattern.name}</p>
                      <TriLanguageIfSanskrit
                        item={toTriFromFields({ sanskrit: pattern.sanskrit, english: pattern.description })}
                        meaningPrefix="translation"
                        compact
                        token={token}
                      />
                      {pattern.ratio && <Badge variant="outline" className="mt-1">{pattern.ratio}</Badge>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Adaptive Message */}
          {activeSession.guidance.adapted_message && (
            <Card className="bg-primary/5 border-primary/20 mb-6">
              <CardContent className="p-4">
                <p className="text-sm">{activeSession.guidance.adapted_message}</p>
              </CardContent>
            </Card>
          )}

          {/* Controls */}
          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setIsPlaying(!isPlaying)}
            >
              {isPlaying ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
              {isPlaying ? L.pause : L.resume}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => {
                setIsPlaying(false);
                clearInterval(timerRef.current);
                completeSession();
                setActiveSession(null);
                setSelectedMode(null);
              }}
            >
              <Square className="w-5 h-5 mr-2" />
              {L.endSession}
            </Button>
          </div>

          {/* Closing Blessing */}
          <div className="mt-8">
            <TriLanguageBlock
              item={toTriFromFields({ english: activeSession.guidance.closing_blessing, meaning_en: activeSession.guidance.closing_blessing })}
              meaningPrefix="meaning"
              meaningAudioMode="sadhana"
              token={token}
              className="text-muted-foreground italic text-center"
            />
          </div>
        </div>
      </div>
    );
  }

  // Mode Selection View
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="prayer-hall-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <Moon className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-3">{L.sanskritTitle}</h1>
          <h2 className="font-heading text-2xl text-muted-foreground mb-4">{L.pageTitle}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">{L.pageSubtitle}</p>
        </div>

        <div className="max-w-2xl mx-auto mb-10">
          <GlobalChantingEntry compact />
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            <Button size="sm" variant="default" asChild>
              <Link to="/community-hall">Community Hall (LIVE)</Link>
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/global-chanting">All global chants</Link>
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/townhall">VedaPath Townhall</Link>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setMandirChantId("lalita_sahasranama")}
              data-testid="mandir-private-chant"
            >
              Private chanting (Mandir)
            </Button>
          </div>
        </div>

        {mandirChantId && (
          <DhyanaMandirChantingOverlay
            token={token}
            chantId={mandirChantId}
            onClose={() => setMandirChantId(null)}
          />
        )}

        {/* Duration Selector */}
        <Card className="bg-white/90 backdrop-blur-sm mb-8 max-w-md mx-auto">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{L.sessionDuration}</span>
              <div className="flex gap-2">
                {[5, 10, 15, 20, 30, 45].map((d) => (
                  <Button
                    key={d}
                    variant={duration === d ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDuration(d)}
                  >
                    {d}m
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seeker State Selector */}
        {seekerStates && (
          <Card className="bg-white/90 backdrop-blur-sm mb-8 max-w-2xl mx-auto">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                {L.currentState}
              </CardTitle>
              <p className="text-sm text-muted-foreground">{L.stateHint}</p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(seekerStates).map(([id, state]) => (
                  <Button
                    key={id}
                    variant={userSeekerState === id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUserSeekerState(id)}
                    className="gap-1"
                  >
                    {state.name}
                    <span className="text-xs opacity-70">{state.sanskrit}</span>
                  </Button>
                ))}
              </div>
              {seekerStates[userSeekerState] && (
                <p className="mt-3 text-sm text-muted-foreground italic">
                  {seekerStates[userSeekerState].guidance_style}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {token && customMantras.length > 0 && (
          <Card className="bg-white/90 backdrop-blur-sm mb-8 max-w-2xl mx-auto" data-testid="custom-mantra-picker">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-primary" />
                My Mantras
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Use one of your saved mantras as the focus for this session.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedCustomMantra ? "outline" : "default"}
                  size="sm"
                  onClick={() => setSelectedCustomMantra(null)}
                >
                  None
                </Button>
                {customMantras.map((m) => (
                  <Button
                    key={m.id}
                    variant={selectedCustomMantra?.id === m.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCustomMantra(m)}
                    className="font-sanskrit"
                  >
                    {m.text.slice(0, 40)}
                  </Button>
                ))}
              </div>
              {selectedCustomMantra && (
                <div className="mt-3">
                  <MantraTriLanguageBlock item={mantraToTriItem(selectedCustomMantra)} token={token} compact />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Practice Modes Grid */}
        {modes && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(modes).map(([id, mode]) => {
              const ModeIcon = modeIcons[id] || Moon;
              const isRecommended = seekerStates?.[userSeekerState]?.recommended_modes?.includes(id);
              
              return (
                <Card
                  key={id}
                  className={`bg-white/90 backdrop-blur-sm transition-all hover:shadow-lg cursor-pointer ${
                    isRecommended ? "ring-2 ring-primary/30" : ""
                  }`}
                  onClick={() => startSession(id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="p-3 rounded-full bg-primary/10">
                        <ModeIcon className="w-6 h-6 text-primary" />
                      </div>
                      {isRecommended && (
                        <Badge className="bg-primary/10 text-primary">{L.recommended}</Badge>
                      )}
                    </div>
                    <CardTitle className="font-heading text-xl mt-3">
                      {mode.name}
                    </CardTitle>
                    <TriLanguageIfSanskrit
                      item={toTriFromFields({ sanskrit: mode.sanskrit, english: mode.description })}
                      meaningPrefix="translation"
                      compact
                    />
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground text-sm mb-4">
                      {mode.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Default: {mode.default_duration} min
                      </span>
                      <Button size="sm" className="gap-1">
                        <Play className="w-4 h-4" />
                        {L.begin}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Footer Blessing */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground italic">
            {L.footerShanti}
          </p>
        </div>
      </div>
    </div>
  );
};

const PrayerHallPage = ({ token }) => (
  <CmsPageShell slug="prayer-hall" layout="embedded">
    <PrayerHallPageInner token={token} />
  </CmsPageShell>
);

// ==================== MOBILE APP BANNER ====================

const MobileAppBanner = () => {
  const L = useCmsLabels();
  const [showQR, setShowQR] = useState(false);
  const playStoreUrl = "#";
  
  return (
    <div className="bg-gradient-to-r from-primary/10 to-secondary/10 py-8 px-4 border-y border-border/30">
      <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-full bg-primary/10">
            <Smartphone className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-medium">{L.mobileBannerTitle}</h3>
            <p className="text-muted-foreground">{L.mobileBannerBody}</p>
          </div>
        </div>
       
      </div>
      
      {showQR && (
        <div className="max-w-4xl mx-auto mt-6 flex justify-center">
          <div className="p-4 bg-white rounded-xl shadow-card">
            <p className="text-center text-sm text-muted-foreground mb-2">Scan to download</p>
            {/* Simple QR code placeholder - in production use a QR library */}
            <div className="w-32 h-32 bg-muted flex items-center justify-center rounded-lg">
              <QrCode className="w-16 h-16 text-muted-foreground" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function SacredAdminShell({ children }) {
  return (
    <SacredAdminErrorBoundary>
      <div className="App font-body min-h-screen bg-background text-foreground">{children}</div>
    </SacredAdminErrorBoundary>
  );
}

function MainSite({ auth, crisisOpen, setCrisisOpen }) {
  return (
    <I18nProvider user={auth.user} token={auth.token} updateProfile={auth.updateProfile}>
      <CrisisModeModal open={crisisOpen} onClose={() => setCrisisOpen(false)} />
      <AppNavigation
        user={auth.user}
        logout={auth.logout}
        logo={VedaPathLogo}
        onCrisisHelp={() => setCrisisOpen(true)}
      />
      <GlobalErrorBanner />
      <SacredStaffBar />
      <main className="pt-16">
        <Routes>
            <Route path="/" element={<LandingPage user={auth.user} />} />
            <Route path="/knowledge-base" element={<Navigate to="/knowledge" replace />} />
            <Route path="/about" element={<CmsPageShell slug="about" layout="document" />} />
            <Route path="/contact" element={<CmsPageShell slug="contact" layout="document" />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/auth/verify" element={<Navigate to="/verify-email" replace />} />
            <Route path="/auth/reset" element={<Navigate to="/reset-password" replace />} />
            <Route path="/auth" element={<Navigate to="/login" replace />} />
            <Route
              path="/settings/account"
              element={
                <AccountSettingsPage user={auth.user} token={auth.token} onLogout={auth.logout} />
              }
            />
            <Route path="/settings/security" element={<SecuritySettingsPage />} />
            <Route
              path="/contact-acharya"
              element={<ContactAcharyaPage user={auth.user} token={auth.token} />}
            />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/copyright" element={<LegalPage />} />
            <Route path="/dmca" element={<LegalPage />} />
            <Route path="/diagnostic" element={<DiagnosticPage token={auth.token} setUser={auth.setUser} />} />
            <Route path="/knowledge" element={<KnowledgePage token={auth.token} />} />
            <Route path="/library" element={<SacredLibraryHub />} />
            <Route path="/library/markandeya-puranam" element={<MarkandeyaPuranamPage />} />
            <Route path="/markandeya-puranam" element={<Navigate to="/library/markandeya-puranam" replace />} />
            <Route path="/library/markandeya" element={<Navigate to="/library/markandeya-puranam" replace />} />
            <Route path="/library/stories/markandeya-puranam" element={<Navigate to="/library/markandeya-puranam" replace />} />
            <Route path="/scriptures" element={<Navigate to="/library/scriptures" replace />} />
            <Route path="/gita" element={<Navigate to="/gitas" replace />} />
            <Route path="/library/deities/:id" element={<SacredTopicPage topicType="deity" token={auth.token} />} />
            <Route path="/library/festivals/:id" element={<SacredTopicPage topicType="festival" token={auth.token} />} />
            <Route path="/library/vratas/:id" element={<SacredTopicPage topicType="vrata" token={auth.token} />} />
            <Route path="/library/stories/:id" element={<SacredTopicPage topicType="story" token={auth.token} />} />
            <Route path="/library/sadhanas/:id" element={<SacredTopicPage topicType="sadhana" token={auth.token} />} />
            <Route path="/gitas" element={<GitaHub />} />
            <Route path="/gitas/:id" element={<GitaPage token={auth.token} />} />
            <Route path="/library/deities/:id/edit" element={<SacredEditor topicType="deity" />} />
            <Route path="/library/festivals/:id/edit" element={<SacredEditor topicType="festival" />} />
            <Route path="/library/vratas/:id/edit" element={<SacredEditor topicType="vrata" />} />
            <Route path="/library/stories/:id/edit" element={<SacredEditor topicType="story" />} />
            <Route path="/library/sadhanas/:id/edit" element={<SacredEditor topicType="sadhana" />} />
            <Route path="/library/scriptures" element={<ScripturesLibraryPage token={auth.token} />} />
            <Route path="/panchanga" element={<PanchangaPage token={auth.token} />} />
            <Route path="/prayer-hall" element={<PrayerHallPage token={auth.token} />} />
            <Route path="/global-chanting" element={<GlobalChantingPage />} />
            <Route path="/global-chanting/:chantId" element={<GlobalChantingRoom token={auth.token} />} />
            <Route path="/community-hall" element={<CommunityHallPage />} />
            <Route
              path="/townhall"
              element={
                <ErrorBoundary>
                  <TownhallPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/townhall/:chantId/edit"
              element={
                <ErrorBoundary>
                  <TownhallAdminEditor />
                </ErrorBoundary>
              }
            />
            <Route
              path="/townhall/:chantId"
              element={
                <ErrorBoundary>
                  <TownhallChantView token={auth.token} />
                </ErrorBoundary>
              }
            />
            <Route path="/guide" element={<GuidePage token={auth.token} user={auth.user} />} />
            <Route path="/dashboard" element={<RequireAuth><DashboardPage user={auth.user} token={auth.token} /></RequireAuth>} />
            <Route path="/teacher" element={<RequireAuth><TeacherPage user={auth.user} token={auth.token} /></RequireAuth>} />
            <Route path="/foundations" element={<FoundationsPage token={auth.token} />} />
            <Route path="/foundations/:id" element={<FoundationsPage token={auth.token} />} />
            <Route path="/satsanga" element={<SatsangaPage token={auth.token} />} />
            <Route path="/lifestyle" element={<LifestylePage token={auth.token} />} />
            <Route path="/parampara" element={<ParamparaPage token={auth.token} />} />
            <Route path="/sadhana" element={<SadhanaPage token={auth.token} />} />
            <Route path="/journal" element={<JournalPage token={auth.token} />} />
            <Route path="/admin" element={<RequireAdmin><AdminDashboardPage user={auth.user} /></RequireAdmin>} />
            <Route path="/streams" element={<StreamsPage token={auth.token} />} />
            <Route path="/crisis" element={<CrisisPage />} />
            <Route path="/crisis/:id" element={<CrisisPage />} />
            <Route path="/dharma-decisions" element={<DharmaDecisionsPage user={auth.user} />} />
            <Route path="/dharma-decisions/:id" element={<DharmaDecisionsPage user={auth.user} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <GlobalFooter />
    </I18nProvider>
  );
}

// Main App Component
function AppRoutes() {
  const auth = useAppAuth();
  const [crisisOpen, setCrisisOpen] = useState(false);

  useEffect(() => {
    installAcharyaUiErrorLogging();
  }, []);

  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin/sacred" element={<RequireAdmin><SacredAdminShell><AdminPanel /></SacredAdminShell></RequireAdmin>} />
        <Route path="/admin/tattva-test" element={<RequireAdmin><SacredAdminShell><TattvaTestPage /></SacredAdminShell></RequireAdmin>} />
        <Route path="/admin/alerts-test" element={<RequireAdmin><SacredAdminShell><AlertsTestPage /></SacredAdminShell></RequireAdmin>} />
        <Route path="/admin/journey-test" element={<RequireAdmin><SacredAdminShell><JourneyTestPage /></SacredAdminShell></RequireAdmin>} />
        <Route path="/admin/journey-validation" element={<RequireAdmin><SacredAdminShell><JourneyValidationPage /></SacredAdminShell></RequireAdmin>} />
        <Route path="/admin/acharya-test" element={<RequireAdmin><AcharyaTest user={auth.user} token={auth.token} /></RequireAdmin>} />
        <Route path="/admin/setup" element={<RequireAdmin><SacredAdminShell><SacredAdminStandalone /></SacredAdminShell></RequireAdmin>} />
        <Route path="/admin/pages" element={<RequireAdmin><SacredAdminShell><SacredPagesHub /></SacredAdminShell></RequireAdmin>} />
        <Route
          path="/admin/pages/:slug/edit"
          element={
            <RequireAdmin>
              <SacredAdminShell>
                <PageEditor />
              </SacredAdminShell>
            </RequireAdmin>
          }
        />
        <Route path="/*" element={<MainSite auth={auth} crisisOpen={crisisOpen} setCrisisOpen={setCrisisOpen} />} />
      </Routes>
    </BrowserRouter>
  );
}

// ==================== CRISIS GUIDANCE PAGE ====================
function CrisisPageInner() {
  const L = useCmsLabels();
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (id) {
      axios.get(`${API}/crisis/${id}`).then((r) => setDetail(r.data)).catch(() => {});
    } else {
      axios.get(`${API}/crisis`).then((r) => setItems(r.data.items || [])).catch(() => {});
    }
  }, [id]);
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="crisis-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-heading text-4xl mb-2">{L.pageTitle}</h1>
        <p className="text-sm text-muted-foreground mb-8">{L.disclaimer}</p>
        {!id && (
          <div className="space-y-3">
            {items.map((c) => (
              <Card key={c.id} className="bg-white/90 cursor-pointer hover:shadow-md" onClick={() => window.location.href = `/crisis/${c.id}`}>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">{c.title}</CardTitle>
                  <p className="text-sm text-muted-foreground">{c.summary}</p>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
        {detail && (
          <Card className="bg-white/90">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">{detail.title}</CardTitle>
              <p className="text-muted-foreground">{detail.summary}</p>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="font-medium mb-1">{L.immediateSteps}</p>
                <ul className="list-disc list-inside text-muted-foreground">{detail.immediate_steps?.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
              <p className="leading-relaxed">{detail.dharma_orientation}</p>
              {detail.body && <p className="leading-relaxed whitespace-pre-wrap mt-4">{detail.body}</p>}
              {detail.dharma_principle && <p className="italic text-primary mt-2"><strong>{L.dharmaPrinciple}</strong> {detail.dharma_principle}</p>}
              {detail.grounding_practice && (
                <div className="mt-2">
                  <p className="font-medium">{detail.grounding_practice.title}</p>
                  <ul className="list-disc list-inside text-muted-foreground">{detail.grounding_practice.steps?.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
              {detail.mantra && (
                <MantraTriLanguageBlock item={mantraToTriItem(detail.mantra)} meaningPrefix="translation" />
              )}
              {detail.what_not_to_do?.length > 0 && (
                <div className="mt-2"><p className="font-medium">{L.whatNotToDo}</p><ul className="list-disc list-inside">{detail.what_not_to_do.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
              )}
              {detail.one_minute?.length > 0 && <div className="mt-2"><p className="font-medium">{L.oneMinute}</p><ul className="list-disc list-inside">{detail.one_minute.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
              {detail.ten_minutes?.length > 0 && <div className="mt-2"><p className="font-medium">{L.tenMinutes}</p><ul className="list-disc list-inside">{detail.ten_minutes.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
              {detail.thirty_minutes?.length > 0 && <div className="mt-2"><p className="font-medium">{L.thirtyMinutes}</p><ul className="list-disc list-inside">{detail.thirty_minutes.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
              {detail.professional_note && <p className="text-xs italic text-amber-800 mt-4">{detail.professional_note}</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function CrisisPage() {
  return (
    <CmsPageShell slug="help-now" layout="embedded">
      <CrisisPageInner />
    </CmsPageShell>
  );
}

// ==================== DHARMA DECISIONS PAGE ====================
function DharmaDecisionsPage({ user }) {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    const state = user?.seeker_state ? `?seeker_state=${encodeURIComponent(user.seeker_state)}` : "";
    if (id) {
      axios.get(`${API}/dharma/decision/${id}${state}`).then((r) => setDetail(r.data)).catch(() => {});
    } else {
      axios.get(`${API}/dharma/decision`).then((r) => setItems(r.data.items || [])).catch(() => {});
    }
  }, [id, user?.seeker_state]);
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="dharma-decisions-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-heading text-4xl mb-8">Dharma Decisions</h1>
        {!id && items.map((d) => (
          <Card key={d.id} className="bg-white/90 mb-3 cursor-pointer" onClick={() => window.location.href = `/dharma-decisions/${d.id}`}>
            <CardHeader>
              <CardTitle className="text-lg">{d.situation}</CardTitle>
              <p className="text-sm text-muted-foreground">{d.summary}</p>
            </CardHeader>
          </Card>
        ))}
        {detail && (
          <Card className="bg-white/90">
            <CardHeader>
              <CardTitle>{detail.situation}</CardTitle>
              {detail.state_note && <p className="text-sm text-primary italic">{detail.state_note}</p>}
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p><strong>Principle:</strong> {detail.dharma_principle}</p>
              <ul className="list-disc list-inside">{detail.practical_steps?.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ==================== FOUNDATIONS PAGE ====================
function FoundationsPage({ token }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const openFoundation = (f) => {
    setSelected(f);
    trackJourneyEvent(token, "foundation_view", f.id, f.title);
  };
  useEffect(() => {
    axios.get(`${API}/foundations`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="foundations-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-3">Foundations</h1>
          <p className="text-muted-foreground">The saṅkalpa, principles, and vision of VedaPath.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {items.map((f) => (
            <Card
              key={f.id}
              className="cursor-pointer hover:shadow-float bg-white/80"
              onClick={() => openFoundation(f)}
              data-testid={`foundation-card-${f.id}`}
            >
              <CardHeader>
                <CardTitle className="font-heading">{f.title}</CardTitle>
                <TriLanguageIfSanskrit
                  item={toTriFromFields({ sanskrit: f.sanskrit, english: f.summary })}
                  meaningPrefix="summary"
                  compact
                />
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{f.summary}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        {selected && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)}>
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-auto bg-white" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="font-heading text-2xl">{selected.title}</CardTitle>
                <TriLanguageIfSanskrit
                  item={toTriFromFields({ sanskrit: selected.sanskrit, english: selected.summary })}
                  meaningPrefix="summary"
                  compact
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="italic text-primary">"{selected.summary}"</p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{selected.body}</p>
                {selected.stories?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Stories</p>
                    {selected.stories.map((s, i) => (
                      <div key={i} className="border-l-2 border-primary/40 pl-3 mb-3 text-sm">
                        <p className="font-medium">{s.title}</p>
                        {s.source && <p className="text-xs text-muted-foreground">{s.source}</p>}
                        <p className="text-muted-foreground mt-1">{s.summary}</p>
                      </div>
                    ))}
                  </div>
                )}
                {selected.daily_application?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Daily Application</p>
                    <ul className="text-sm space-y-1">
                      {selected.daily_application.map((d, i) => <li key={i}>• {d}</li>)}
                    </ul>
                  </div>
                )}
                {selected.mistakes_to_avoid?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Mistakes to Avoid</p>
                    <ul className="text-sm space-y-1">
                      {selected.mistakes_to_avoid.map((m, i) => <li key={i}>⚠ {m}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== SATSANGA PAGE ====================
function SatsangaPageInner({ token }) {
  const L = useCmsLabels();
  const [data, setData] = useState(null);
  const [intro, setIntro] = useState(null);
  const [audience, setAudience] = useState("all");
  const [reflection, setReflection] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    axios.get(`${API}/satsanga/intro`).then((r) => setIntro(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const url = audience === "all" ? `${API}/satsanga` : `${API}/satsanga/${audience}`;
    axios.get(url).then((r) => setData(r.data)).catch(() => {});
  }, [audience]);
  useEffect(() => {
    if (token) trackJourneyEvent(token, "satsanga_view", `satsanga:${audience}`, `Satsaṅga (${audience})`);
  }, [token, audience]);
  const topic = data?.current || data?.items?.[0];
  const submitReflection = async () => {
    if (!token || !topic || !reflection.trim()) return;
    try {
      await axios.post(
        `${API}/user/reflections`,
        { topic_id: topic.id, text: reflection },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaved(true);
      setReflection("");
    } catch (e) {
      console.error("save reflection failed:", e);
    }
  };
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="satsanga-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-2">{L.pageTitle}</h1>
          <p className="text-muted-foreground">{L.pageSubtitle}</p>
        </div>
        {intro && (
          <Card className="bg-white/90 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl">{intro.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{intro.definition}</p>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{intro.vedapath_digital}</CardContent>
          </Card>
        )}
        <div className="flex justify-center gap-2 mb-6 flex-wrap">
          {[
            { id: "all", label: L.audienceAll },
            { id: "children", label: L.audienceChildren },
            { id: "elders", label: L.audienceElders },
            { id: "struggling", label: L.audienceStruggling },
          ].map((a) => (
            <Button key={a.id} variant={audience === a.id ? "default" : "outline"} size="sm" onClick={() => setAudience(a.id)}>
              {a.label}
            </Button>
          ))}
        </div>
        {topic && (
          <Card className="bg-white/90 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-2xl">{topic.title}</CardTitle>
              <TriLanguageIfSanskrit
                item={toTriFromFields({ sanskrit: topic.sanskrit, english: topic.title })}
                meaningPrefix="translation"
                compact
                token={token}
              />
            </CardHeader>
            <CardContent className="space-y-4">
              <TriLanguageBlock
                item={toTriFromFields({
                  sanskrit: topic.passage,
                  english: topic.reflection,
                })}
                meaningPrefix="translation"
                token={token}
              />
              <p className="text-xs text-muted-foreground">{topic.source}</p>
              <div>
                <p className="text-sm text-muted-foreground mb-2">{L.deepeningQuestions}</p>
                <ul className="text-sm space-y-1">
                  {topic.deepening_questions?.map((q, i) => <li key={i}>{i + 1}. {q}</li>)}
                </ul>
              </div>
              {token && (
                <div className="mt-4">
                  <Label htmlFor="reflection">{L.myReflection}</Label>
                  <textarea
                    id="reflection"
                    value={reflection}
                    onChange={(e) => { setReflection(e.target.value); setSaved(false); }}
                    className="w-full mt-1 p-3 rounded-md border border-border/80 bg-white/70 min-h-[120px]"
                    placeholder={L.reflectionPlaceholder}
                  />
                  <div className="flex items-center gap-3 mt-2">
                    <Button onClick={submitReflection} disabled={!reflection.trim()}>{L.save}</Button>
                    {saved && <span className="text-sm text-muted-foreground">{L.savedReflection}</span>}
                  </div>
                </div>
              )}
              {!token && <p className="text-sm text-muted-foreground">{L.signInReflection}</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function SatsangaPage({ token }) {
  return (
    <CmsPageShell slug="satsanga" layout="embedded">
      <SatsangaPageInner token={token} />
    </CmsPageShell>
  );
}

// ==================== LIFESTYLE PAGE ====================
function LifestylePage({ token }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    axios.get(`${API}/lifestyle`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (token) trackJourneyEvent(token, "lifestyle_view", "lifestyle", "Lifestyle");
  }, [token]);
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="lifestyle-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-3">Dharmic Lifestyle</h1>
          <p className="text-muted-foreground">Wealth, work, family, food, modern integration.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((l) => (
            <Card key={l.id} className="cursor-pointer hover:shadow-float bg-white/80" onClick={() => setSelected(l)}>
              <CardHeader>
                <CardTitle className="font-heading text-lg">{l.title}</CardTitle>
                <Badge variant="secondary" className="w-fit">{l.category}</Badge>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{l.summary}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        {selected && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)}>
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-auto bg-white" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="font-heading text-2xl">{selected.title}</CardTitle>
                <Badge>{selected.category}</Badge>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="italic text-primary">"{selected.summary}"</p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{selected.body}</p>
                {selected.do_list?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Do</p>
                    <ul className="text-sm space-y-1">{selected.do_list.map((d, i) => <li key={i}>✓ {d}</li>)}</ul>
                  </div>
                )}
                {selected.dont_list?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Don't</p>
                    <ul className="text-sm space-y-1">{selected.dont_list.map((d, i) => <li key={i}>✗ {d}</li>)}</ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== PARAMPARĀ PAGE ====================
function ParamparaPage({ token }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/parampara`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (token) trackJourneyEvent(token, "parampara_view", "parampara", "Paramparā");
  }, [token]);
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="parampara-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-3">Paramparā</h1>
          <p className="text-muted-foreground">Charter, governance, and the 100-year stewardship.</p>
        </div>
        <div className="space-y-6">
          {items.map((p) => (
            <Card key={p.id} className="bg-white/90">
              <CardHeader>
                <CardTitle className="font-heading text-xl">{p.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm leading-relaxed">{p.body}</p>
                <ul className="text-sm space-y-1">{p.bullets?.map((b, i) => <li key={i}>• {b}</li>)}</ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== STREAMS PAGE ====================
function StreamsPage({ token }) {
  const [streams, setStreams] = useState([]);
  const [darshanas, setDarshanas] = useState([]);
  const [panch, setPanch] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/streams`),
      axios.get(`${API}/streams/darshanas`),
      axios.get(`${API}/streams/panchayatana`),
    ]).then(([s, d, p]) => {
      setStreams(s.data.items || []);
      setDarshanas(d.data.items || []);
      setPanch(p.data.items || []);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (token) trackJourneyEvent(token, "stream_view", "streams", "Streams & Darśanas");
  }, [token]);
  const Section = ({ title, items }) => (
    <div className="mb-10">
      <h2 className="font-heading text-2xl mb-4">{title}</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((s) => (
          <Card key={s.id} className="cursor-pointer hover:shadow-float bg-white/80" onClick={() => setSelected(s)}>
            <CardHeader>
              <CardTitle className="font-heading text-lg">{s.name}</CardTitle>
              <TriLanguageIfSanskrit
                item={toTriFromFields({ sanskrit: s.sanskrit, english: s.summary })}
                meaningPrefix="summary"
                compact
              />
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{s.summary}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="streams-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-3">Streams & Darśanas</h1>
          <p className="text-muted-foreground">Hindu streams, Pañcāyatana, and the six classical philosophies.</p>
        </div>
        <Section title="Major Streams" items={streams} />
        <Section title="Six Darśanas" items={darshanas} />
        <Section title="Pañcāyatana" items={panch} />
        {selected && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)}>
            <Card className="max-w-3xl w-full max-h-[90vh] overflow-auto bg-white" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="font-heading text-2xl">{selected.name}</CardTitle>
                <TriLanguageIfSanskrit
                  item={toTriFromFields({ sanskrit: selected.sanskrit, english: selected.summary })}
                  meaningPrefix="summary"
                  compact
                />
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="italic text-primary">"{selected.summary}"</p>
                <p className="text-sm leading-relaxed">{selected.body}</p>
                {selected.modern_application?.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Modern Application</p>
                    <ul className="text-sm space-y-1">{selected.modern_application.map((m, i) => <li key={i}>• {m}</li>)}</ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== DHARMA JOURNAL ====================
function JournalPage({ token }) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    const r = await axios.get(`${API}/user/journal`, { headers: { Authorization: `Bearer ${token}` } });
    setEntries(r.data.entries || []);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!token || !text.trim()) return;
    const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
    if (editing) {
      await axios.post(
        `${API}/user/journal/edit`,
        { id: editing.id, text, tags: tagList },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      await axios.post(
        `${API}/user/journal/add`,
        { text, tags: tagList },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
    setText("");
    setTags("");
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!token) return;
    await axios.delete(`${API}/user/journal/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <p>Sign in to keep your Dharma Journal.</p>
          <Button className="mt-4" onClick={() => navigate("/login")}>Sign In</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="journal-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-heading text-4xl font-medium mb-2 flex items-center gap-2">
          <PenLine className="w-8 h-8 text-primary" /> Dharma Journal
        </h1>
        <p className="text-muted-foreground mb-8">Private reflections tagged with your seeker state at the time.</p>
        <Card className="bg-white/90 mb-6">
          <CardContent className="p-4 space-y-3">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full min-h-[120px] p-3 rounded-md border border-border/80"
              placeholder="What is alive in your heart today?"
            />
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags (comma-separated)" />
            <div className="flex gap-2">
              <Button onClick={save}>{editing ? "Update" : "Add entry"}</Button>
              {editing && <Button variant="outline" onClick={() => { setEditing(null); setText(""); setTags(""); }}>Cancel</Button>}
            </div>
          </CardContent>
        </Card>
        <div className="space-y-4">
          {entries.map((e) => (
            <Card key={e.id} className="bg-white/90">
              <CardContent className="p-4">
                <p className="text-sm whitespace-pre-wrap">{e.text}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {e.tags?.map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
                  <Badge variant="outline">{e.seeker_state_at_time}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{new Date(e.created_at).toLocaleString()}</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(e); setText(e.text); setTags((e.tags || []).join(", ")); }}>Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => remove(e.id)}>Delete</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminDashboardPage({ user }) {
  const [adminKey, setAdminKey] = useState(localStorage.getItem("vedaPath_admin_key") || "");
  const [health, setHealth] = useState(null);
  const [activity, setActivity] = useState(null);
  const [errors, setErrors] = useState(null);
  const navigate = useNavigate();

  const headers = useMemo(() => ({ "X-Admin-Key": adminKey }), [adminKey]);

  const refresh = useCallback(async () => {
    if (!adminKey) return;
    localStorage.setItem("vedaPath_admin_key", adminKey);
    const [h, a, e] = await Promise.all([
      axios.get(`${API}/admin/health`, { headers }),
      axios.get(`${API}/admin/activity`, { headers }),
      axios.get(`${API}/admin/errors`, { headers }),
    ]);
    setHealth(h.data);
    setActivity(a.data);
    setErrors(e.data);
  }, [adminKey, headers]);

  if (!canAccessAdminRoutes(user?.role) && user?.role !== "teacher") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8"><p>Admin access required.</p><Button className="mt-4" onClick={() => navigate("/login")}>Sign in</Button></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4" data-testid="admin-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <h1 className="font-heading text-3xl">Dharma OS Health</h1>
        <p className="text-sm text-muted-foreground">
          Signed in as <strong>{user?.email}</strong> ({user?.role}). To edit library topics and pages, go to{" "}
          <a href="/admin/sacred" className="text-primary underline">Content Admin</a> or{" "}
          <a href="/library" className="text-primary underline">Sacred Library</a> — no Admin API key needed for that.
        </p>
        <Card>
          <CardContent className="p-4 space-y-2">
            <p className="text-xs text-muted-foreground">
              Optional: <strong>Admin API key</strong> is a Cloudflare Worker secret for server metrics only (not your login password).
            </p>
            <div className="flex gap-2">
            <Input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Admin API key (optional)" className="flex-1" />
            <Button onClick={refresh}>Load metrics</Button>
            </div>
          </CardContent>
        </Card>
        {health && (
          <Card><CardHeader><CardTitle>Health</CardTitle></CardHeader>
            <CardContent className="text-sm">Status: {health.status} · v{health.version} · KV: {health.kv_bound ? "yes" : "no"}</CardContent>
          </Card>
        )}
        {activity && (
          <Card><CardHeader><CardTitle>Activity ({activity.request_count})</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1 max-h-48 overflow-auto">
              {activity.last_requests?.map((r, i) => <p key={i}>{r.at} {r.method} {r.path}</p>)}
            </CardContent>
          </Card>
        )}
        {errors && (
          <Card><CardHeader><CardTitle>Errors ({errors.error_count})</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-1 max-h-48 overflow-auto">
              {errors.last_errors?.map((r, i) => <p key={i}>{r.at} {r.path}: {r.message}</p>)}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function SadhanaAnalyticsPanel({ token }) {
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/sadhana/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAnalytics(r.data))
      .catch(() => {});
  }, [token]);
  if (!analytics) return <p className="text-muted-foreground text-sm">Sign in and log sādhana to see analytics.</p>;
  const maxMin = Math.max(1, ...(analytics.weekly_graph?.map((w) => w.minutes) || [1]));
  return (
    <div className="space-y-6" data-testid="sadhana-analytics">
      <div className="grid md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Monthly consistency</p><p className="text-2xl font-heading">{analytics.monthly_consistency}%</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Mindfulness</p><p className="text-2xl font-heading">{analytics.mindfulness_score}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Breath score</p><p className="text-2xl font-heading">{analytics.breath_score}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Prayer Hall sessions</p><p className="text-2xl font-heading">{analytics.prayer_hall_sessions}</p></CardContent></Card>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-lg">Weekly minutes</CardTitle></CardHeader>
        <CardContent className="flex items-end gap-2 h-32">
          {analytics.weekly_graph?.map((w) => (
            <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-primary/70 rounded-t" style={{ height: `${(w.minutes / maxMin) * 100}%`, minHeight: w.minutes ? 4 : 0 }} />
              <span className="text-[10px] text-muted-foreground">{w.week_start.slice(5)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Activity className="w-4 h-4" /> State evolution (activity)</CardTitle></CardHeader>
        <CardContent className="flex gap-0.5 h-12 items-end">
          {analytics.state_evolution_graph?.slice(-30).map((p) => (
            <div key={p.date} className="flex-1 bg-primary/40 rounded-sm" style={{ height: `${p.activity_score * 33}%` }} title={p.date} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== SADHANA TRACKER PAGE ====================
function SadhanaPageInner({ token }) {
  const L = useCmsLabels();
  const [history, setHistory] = useState(null);
  const [guidelines, setGuidelines] = useState([]);
  const [mantras, setMantras] = useState({ open: [], guru_required: [] });
  const [practice, setPractice] = useState("Mantra Japa");
  const [minutes, setMinutes] = useState(15);
  const [notes, setNotes] = useState("");
  const [global, setGlobal] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [customMantras, setCustomMantras] = useState([]);
  const [mantraDraft, setMantraDraft] = useState({ text: "", meaning: "", category: "personal", count_goal: 108 });
  const [moksha, setMoksha] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/user/sadhana/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(r.data);
    } catch (e) { console.error(e); }
  }, [token]);

  const refreshCustomMantras = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/user/mantras`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomMantras(r.data.mantras || []);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => {
    refresh();
    refreshCustomMantras();
    axios.get(`${API}/sadhana/guidelines`).then((r) => setGuidelines(r.data.items || []));
    axios.get(`${API}/sadhana/mantras`).then((r) => setMantras(r.data));
    axios.get(`${API}/sadhana/global`).then((r) => setGlobal(r.data));
    axios.get(`${API}/sadhana/festivals`).then((r) => setFestivals(r.data.items || []));
    axios.get(`${API}/sadhana/moksha`).then((r) => setMoksha(r.data)).catch(() => {});
  }, [refresh, refreshCustomMantras]);

  const addMantra = async () => {
    if (!token) return;
    if (!mantraDraft.text.trim()) return;
    try {
      await axios.post(
        `${API}/user/mantras/add`,
        { ...mantraDraft, count_goal: Number(mantraDraft.count_goal) || 108 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMantraDraft({ text: "", meaning: "", category: "personal", count_goal: 108 });
      refreshCustomMantras();
    } catch (e) { console.error(e); }
  };

  const deleteMantra = async (id) => {
    if (!token) return;
    try {
      await axios.delete(`${API}/user/mantras/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      refreshCustomMantras();
    } catch (e) { console.error(e); }
  };

  const log = async () => {
    if (!token) return;
    try {
      await axios.post(
        `${API}/user/sadhana/log`,
        { practice, minutes: Number(minutes), notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes("");
      refresh();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="sadhana-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-2">{L.pageTitle}</h1>
          <p className="text-muted-foreground">{L.pageSubtitle}</p>
        </div>

        <Tabs defaultValue="practice" className="mb-4">
          <TabsList>
            <TabsTrigger value="practice">{L.tabPractice}</TabsTrigger>
            <TabsTrigger value="analytics">{L.tabAnalytics}</TabsTrigger>
          </TabsList>
          <TabsContent value="analytics" className="mt-6">
            <SadhanaAnalyticsPanel token={token} />
          </TabsContent>
          <TabsContent value="practice" className="mt-6 space-y-6">

        {global && (
          <Card className="bg-white/80 mb-6">
            <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <p className="text-sm text-muted-foreground">{L.globalParticipation}</p>
                <p className="font-heading text-2xl">{global.participants_estimate.toLocaleString()}</p>
              </div>
              <div className="text-sm text-muted-foreground">{global.note}</div>
            </CardContent>
          </Card>
        )}

        {token ? (
          <Card className="bg-white/90 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">{L.logToday}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="practice">{L.practiceLabel}</Label>
                  <Input id="practice" value={practice} onChange={(e) => setPractice(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="minutes">{L.minutes}</Label>
                  <Input id="minutes" type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="notes">{L.notesOptional}</Label>
                <textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full p-3 rounded-md border border-border/80 bg-white/70" />
              </div>
              <Button onClick={log}>{L.log}</Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white/80 mb-6">
            <CardContent className="p-4 text-sm text-muted-foreground">{L.signInToLog}</CardContent>
          </Card>
        )}

        {history && (
          <Card className="bg-white/90 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">{L.dashboard}</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              <div><p className="text-sm text-muted-foreground">{L.totalMinutes}</p><p className="font-heading text-2xl">{history.total_minutes}</p></div>
              <div><p className="text-sm text-muted-foreground">{L.daysPracticed}</p><p className="font-heading text-2xl">{history.days_practiced}</p></div>
              <div><p className="text-sm text-muted-foreground">{L.currentStreak}</p><p className="font-heading text-2xl">{history.current_streak}</p></div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-white/90 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Mantras (no guru needed)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {mantras.open.map((m) => (
              <div key={m.id} className="border-l-2 border-primary/40 pl-3 text-sm">
                <MantraTriLanguageBlock item={mantraToTriItem(m)} token={token} meaningPrefix="translation" />
              </div>
            ))}
          </CardContent>
        </Card>

        {token && (
          <Card className="bg-white/90 mb-6" data-testid="my-mantras-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">My Mantras</CardTitle>
              <p className="text-sm text-muted-foreground">
                Personal, ancestral, or guru-given mantras. Stored privately with your profile.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label htmlFor="mantra-text">Mantra text</Label>
                  <Input
                    id="mantra-text"
                    value={mantraDraft.text}
                    onChange={(e) => setMantraDraft({ ...mantraDraft, text: e.target.value })}
                    placeholder="ॐ नमः शिवाय"
                    className="font-sanskrit"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="mantra-meaning">Meaning (optional)</Label>
                  <Input
                    id="mantra-meaning"
                    value={mantraDraft.meaning}
                    onChange={(e) => setMantraDraft({ ...mantraDraft, meaning: e.target.value })}
                    placeholder="Salutations to Śiva — the auspicious indweller"
                  />
                </div>
                <div>
                  <Label htmlFor="mantra-category">Category</Label>
                  <select
                    id="mantra-category"
                    value={mantraDraft.category}
                    onChange={(e) => setMantraDraft({ ...mantraDraft, category: e.target.value })}
                    className="w-full p-2 rounded-md border border-border/80 bg-white/70"
                  >
                    <option value="personal">Personal</option>
                    <option value="guru-given">Guru-given</option>
                    <option value="ancestral">Ancestral</option>
                    <option value="open">Open / general</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="mantra-goal">Daily count goal</Label>
                  <Input
                    id="mantra-goal"
                    type="number"
                    min={1}
                    value={mantraDraft.count_goal}
                    onChange={(e) => setMantraDraft({ ...mantraDraft, count_goal: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={addMantra} disabled={!mantraDraft.text.trim()}>Save mantra</Button>

              {customMantras.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  No saved mantras yet. Add your daily anchor above.
                </p>
              ) : (
                <div className="space-y-3">
                  {customMantras.map((m) => (
                    <div key={m.id} className="border-l-2 border-primary/40 pl-3 text-sm">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1">
                          <MantraTriLanguageBlock item={mantraToTriItem(m)} token={token} meaningPrefix="translation" />
                          <p className="text-xs text-muted-foreground/80 mt-1">
                            {m.category} · daily goal {m.count_goal}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteMantra(m.id)}>
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="bg-white/80 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Mantras requiring guru upadeśa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {mantras.guru_required.map((m) => (
              <div key={m.id}>
                <MantraTriLanguageBlock item={mantraToTriItem(m)} token={token} meaningPrefix="translation" />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white/80 mb-6">
          <CardHeader><CardTitle className="font-heading text-lg">Guidelines</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {guidelines.map((g) => (
              <div key={g.id}>
                <p className="font-medium">{g.title}</p>
                <p className="text-muted-foreground">{g.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white/80 mb-6">
          <CardHeader><CardTitle className="font-heading text-lg">Festival sādhana</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {festivals.map((f) => (
              <div key={f.id}>
                <p className="font-medium">{f.name}</p>
                <p className="text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        {moksha && (
          <Card className="bg-white/80" data-testid="moksha-sadhana-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg">{moksha.title}</CardTitle>
              <p className="text-sm text-muted-foreground italic">{moksha.intro}</p>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="font-medium mb-1">Daily</p>
                <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                  {moksha.daily_practice.map((d, i) => <li key={i}>{renderMantraText(d) ?? d}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1">Weekly</p>
                <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                  {moksha.weekly_practice.map((d, i) => <li key={i}>{renderMantraText(d) ?? d}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1">Seasonal</p>
                <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                  {moksha.seasonal_practice.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1">Mindset</p>
                <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                  {moksha.mindset.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1">Common mistakes</p>
                <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                  {moksha.mistakes.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
              <div>
                <p className="font-medium mb-1">Integration with duties</p>
                <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                  {moksha.integration_with_duties.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SadhanaPage({ token }) {
  return (
    <CmsPageShell slug="sadhana" layout="embedded">
      <SadhanaPageInner token={token} />
    </CmsPageShell>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
